import React, { useState, useEffect, createContext, useContext, useRef, Suspense, lazy } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import lottie from "lottie-web";
import { 
  MessageSquare, PhoneOff, Clock, UserX, Languages, Moon, Calendar, Bell, Users, 
  TrendingUp, Shield, Zap, Globe, Settings, Check, ChevronRight, ArrowRight, Play,
  Mail, Sparkles, Building2, BarChart3, RefreshCw, MessageCircle, CalendarCheck, 
  Send, Star, ChevronDown, Phone, Mic, Pause, Waves, Heart, Target, Headphones,
  User, Brain, ThumbsUp, Monitor, FileText, AlertTriangle, ArrowUp, Car, Wrench,
  AlertCircle, Lightbulb, Tag, Link, Lock, ShieldCheck, Server, BadgeCheck
} from "lucide-react";

// ==================== CALENDLY ====================
const CALENDLY_URL = "https://calendly.com/hello-nexumbusiness/30min?hide_gdpr_banner=1&background_color=000000&text_color=ffffff&primary_color=d4e4dd";
let _calendlyScriptLoaded = false;

const loadCalendlyScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (_calendlyScriptLoaded) return Promise.resolve();
  return new Promise((resolve) => {
    if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) {
      _calendlyScriptLoaded = true;
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => { _calendlyScriptLoaded = true; resolve(); };
    s.onerror = resolve;
    document.body.appendChild(s);
  });
};

// Global modal state (shared across all CTA buttons)
let _setCalendlyModalOpen = null;

const openCalendly = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  if (_setCalendlyModalOpen) _setCalendlyModalOpen(true);
  return false;
};

// ==================== CALENDLY MODAL ====================
const CalendlyModal = () => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);

  // Register global setter so any CTA can open this modal
  useEffect(() => {
    _setCalendlyModalOpen = setOpen;
    return () => { _setCalendlyModalOpen = null; };
  }, []);

  // Load script when modal opens
  useEffect(() => {
    if (!open) return;
    loadCalendlyScript().then(() => setScriptReady(true));
    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      data-testid="calendly-modal"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-[480px] mx-3 sm:mx-4 bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/50"
           style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div>
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block">Nexum</span>
            <h3 className="text-sm font-grotesk font-semibold text-white mt-0.5">
              {lang === 'es' ? 'Reservar demo' : 'Book a demo'}
            </h3>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors border border-white/10 hover:border-white/20"
            data-testid="calendly-modal-close"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        {/* Calendly embed */}
        <div className="relative" style={{ height: 'min(700px, 75vh)' }}>
          {scriptReady ? (
            <div
              className="calendly-inline-widget w-full h-full"
              data-url={CALENDLY_URL}
              ref={(el) => {
                if (el && window.Calendly && !el.dataset.initialized) {
                  el.dataset.initialized = 'true';
                  window.Calendly.initInlineWidget({
                    url: CALENDLY_URL,
                    parentElement: el,
                  });
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <span className="text-xs text-zinc-500">
                  {lang === 'es' ? 'Cargando calendario...' : 'Loading calendar...'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== TRANSLATIONS ====================
const translations = {
  es: {
    nav: { capabilities: "Capacidades", how: "Cómo funciona", pricing: "Precios", demo: "Reservar demo" },
    hero: {
      badge: "Empleado Digital",
      title1: "Tu próximo",
      title2: "empleado es",
      title3: "digital.",
      subtitle: "Nexum responde de forma natural, habla varios idiomas, agenda citas, envía recordatorios por WhatsApp y hace seguimiento automático — ayudando a tu negocio a operar con más control, mejor servicio y más ingresos.",
      cta1: "Reservar demo",
      cta2: "Ver en acción",
      trust: "De confianza para negocios de servicios",
      industries: ["Clínicas", "Centros de belleza", "Talleres", "Fisioterapeutas"]
    },
    stats: {
      title: "Resultados que hablan",
      items: [
        { value: "98%", label: "Tasa de respuesta", desc: "en menos de 30 segundos" },
        { value: "3.2x", label: "Más citas", desc: "agendadas automáticamente" },
        { value: "45%", label: "Menos no-shows", desc: "con recordatorios WhatsApp" },
        { value: "24/7", label: "Disponibilidad", desc: "sin costes adicionales" }
      ]
    },
    positioning: {
      overline: "Qué es Nexum",
      title1: "Más que software.",
      title2: "Un empleado digital real.",
      p1: "Nexum no es un chatbot ni una herramienta de automatización simple. Actúa como un verdadero miembro del equipo integrado en tu negocio — gestionando comunicación, citas, recordatorios y seguimiento con consistencia y profesionalidad.",
      p2: "Disponible 24/7. Hablando múltiples idiomas. Sin perder ningún mensaje. Siempre haciendo seguimiento.",
      cards: [
        { label: "Multilingüe", desc: "Habla el idioma de tus clientes" },
        { label: "24/7 Disponible", desc: "Nunca pierde un mensaje" },
        { label: "Auto-agendado", desc: "Programa citas automáticamente" },
        { label: "Seguimiento", desc: "Cultiva las relaciones" }
      ]
    },
    industries: {
      overline: "Para quién es",
      title: "Diseñado para negocios de servicios",
      items: ["Clínicas dentales", "Centros de estética", "Fisioterapeutas", "Talleres mecánicos", "Peluquerías", "Centros médicos"]
    },
    problem: {
      overline: "El Problema",
      title: "La mayoría de negocios pierden dinero por mala atención al cliente.",
      subtitle: "Cada llamada perdida, respuesta lenta y seguimiento olvidado es dinero que se va. El problema no es el esfuerzo — es la capacidad.",
      items: [
        "Llamadas perdidas que nunca se devuelven",
        "Respuestas lentas que pierden clientes",
        "Citas olvidadas y no-shows",
        "Clientes que visitan una vez y no vuelven",
        "Comunicación inconsistente",
        "Barreras de idioma con clientes",
        "Nadie disponible fuera de horario"
      ]
    },
    capabilities: {
      overline: "Capacidades",
      title: "Qué hace Nexum",
      items: [
        { title: "Responde naturalmente", desc: "Conversaciones inteligentes que parecen humanas" },
        { title: "Habla varios idiomas", desc: "Se comunica en el idioma preferido del cliente" },
        { title: "Trabaja 24/7", desc: "Siempre disponible, incluso cuando tu equipo no lo está" },
        { title: "Agenda citas", desc: "Programa reuniones directamente en tu calendario" },
        { title: "Envía recordatorios", desc: "Notificaciones WhatsApp que reducen no-shows" },
        { title: "Hace seguimiento", desc: "Check-ins automáticos que fidelizan" },
        { title: "Recupera clientes", desc: "Re-engancha a clientes que no han vuelto" },
        { title: "Eleva tu marca", desc: "Imagen profesional con comunicación consistente" }
      ]
    },
    howItWorks: {
      overline: "Proceso",
      title: "Cómo funciona Nexum en tu negocio",
      steps: [
        { num: "01", title: "El cliente contacta", desc: "Un cliente escribe por WhatsApp, llama o envía un mensaje a tu negocio." },
        { num: "02", title: "Nexum responde", desc: "La IA entiende el contexto, responde en el idioma del cliente y guía la conversación." },
        { num: "03", title: "Obtienes resultados", desc: "Citas agendadas, recordatorios programados, seguimientos automatizados. Todo sin mover un dedo." }
      ]
    },
    testimonials: {
      overline: "Testimonios",
      title: "Lo que dicen nuestros clientes",
      items: [
        { text: "Nexum ha transformado completamente cómo gestionamos las citas. Antes perdíamos llamadas constantemente, ahora cada cliente recibe respuesta inmediata.", author: "María García", role: "Directora, Clínica Dental Sonrisa", rating: 5 },
        { text: "El seguimiento automático ha sido un game-changer. Nuestros clientes se sienten atendidos y la tasa de retorno ha subido un 40%.", author: "Carlos Rodríguez", role: "Propietario, AutoTaller Express", rating: 5 },
        { text: "La capacidad multilingüe es increíble. Tenemos clientes de toda Europa y Nexum les atiende en su idioma sin problema.", author: "Sophie Laurent", role: "Gerente, Spa Wellness Center", rating: 5 }
      ]
    },
    dashboard: {
      overline: "Dashboard",
      title: "Un sistema diseñado para operaciones reales",
      subtitle: "Monitoriza citas, conversaciones, recordatorios y actividad de clientes en una interfaz premium.",
      stats: { appointments: "Citas Hoy", conversations: "Conversaciones Activas", reminders: "Recordatorios Enviados" },
      activity: "Actividad Reciente",
      actions: [
        { action: "Cita agendada", client: "María García", lang: "ES" },
        { action: "Recordatorio enviado", client: "John Smith", lang: "EN" },
        { action: "Seguimiento completado", client: "Pierre Dubois", lang: "FR" }
      ]
    },
    value: {
      overline: "Impacto de Negocio",
      title1: "Una mejor experiencia de cliente.",
      title2: "Un negocio más moderno.",
      items: [
        { title: "Menos oportunidades perdidas", desc: "Cada consulta recibe respuesta profesional" },
        { title: "Mejores tiempos de respuesta", desc: "Respuestas instantáneas, a cualquier hora" },
        { title: "Más citas agendadas", desc: "El agendado automático aumenta conversiones" },
        { title: "Mayor retención", desc: "El seguimiento consistente fideliza" },
        { title: "Más orden y control", desc: "Todo registrado en un solo lugar" },
        { title: "Mejor imagen de servicio", desc: "Comunicación moderna y fiable" }
      ]
    },
    personalization: {
      overline: "Configuración",
      title: "Configurado para tu negocio.",
      subtitle: "Nexum se adapta a cómo opera tu negocio. Cada respuesta, cada recordatorio, cada seguimiento está configurado para tus necesidades específicas.",
      configs: ["Horarios de operación", "Servicios ofrecidos", "Reglas de negocio", "Disponibilidad del equipo", "Idiomas preferidos", "Tono de comunicación", "Timing de recordatorios", "Secuencias de seguimiento"],
      preview: "Vista previa de configuración",
      previewItems: [
        { label: "Tipo de negocio", value: "Clínica Dental" },
        { label: "Idiomas", value: "Español, Inglés, Francés" },
        { label: "Tiempo de respuesta", value: "< 30 segundos" },
        { label: "Recordatorios", value: "24h + 2h antes" },
        { label: "Seguimiento", value: "3 días después del servicio" }
      ]
    },
    faq: {
      overline: "Preguntas Frecuentes",
      title: "Todo lo que necesitas saber",
      items: [
        { q: "¿Cuánto tiempo tarda la implementación?", a: "La implementación típica toma entre 5-10 días laborables, incluyendo configuración personalizada, integración con tus sistemas y entrenamiento del equipo." },
        { q: "¿Qué idiomas soporta Nexum?", a: "Nexum soporta más de 20 idiomas incluyendo español, inglés, francés, alemán, italiano, portugués, y muchos más. Los idiomas se pueden configurar según las necesidades de tu negocio." },
        { q: "¿Se integra con mi sistema de citas actual?", a: "Sí, Nexum se integra con la mayoría de sistemas de gestión de citas, calendarios de Google/Outlook, y puede trabajar con tu CRM existente." },
        { q: "¿Qué pasa si el cliente necesita hablar con una persona?", a: "Nexum detecta automáticamente cuándo una conversación requiere intervención humana y transfiere el chat a tu equipo con todo el contexto de la conversación." },
        { q: "¿Hay permanencia o compromiso mínimo?", a: "No hay permanencia. Puedes cancelar el servicio mensualmente. Creemos que la mejor forma de retenerte es con resultados, no con contratos." }
      ]
    },
    pricing: {
      overline: "Inversión",
      title: "Implementación premium para negocios que quieren control real.",
      subtitle: "Esto no es una suscripción de software simple. Es el despliegue completo de un empleado digital — con onboarding dedicado, configuración y optimización continua.",
      note: "Un cliente recuperado paga tu servicio mensual",
      implementation: "Implementación",
      implementationPrice: "€2.650",
      implementationNote: "pago único",
      implementationDesc: "Setup completo, configuración y formación",
      monthly: "Servicio Mensual",
      monthlyPrice: "€169",
      monthlyNote: "/mes",
      monthlyDesc: "Gestión y optimización continua de la IA",
      features: ["Configuración personalizada de IA", "Integración WhatsApp Business", "Sistema de agendado", "Soporte multilingüe", "Automatización de seguimiento", "Soporte prioritario", "Informes mensuales"],
      cta: "Solicitar demo"
    },
    finalCta: {
      title: "Añade Nexum a tu negocio.",
      subtitle: "Descubre cómo Nexum puede trabajar como un miembro real de tu equipo.",
      cta: "Solicitar demo"
    },
    footer: {
      tagline: "Empleado Digital",
      copyright: "Todos los derechos reservados."
    }
  },
  en: {
    nav: { capabilities: "Capabilities", how: "How it works", pricing: "Pricing", demo: "Book a demo" },
    hero: {
      badge: "Digital Employee",
      title1: "Your next",
      title2: "employee is",
      title3: "digital.",
      subtitle: "Nexum answers naturally, speaks multiple languages, books appointments, sends WhatsApp reminders and follows up automatically — helping your business operate with more control, better service and more revenue.",
      cta1: "Book a demo",
      cta2: "See in action",
      trust: "Trusted by service businesses",
      industries: ["Clinics", "Beauty centers", "Workshops", "Physiotherapists"]
    },
    stats: {
      title: "Results that speak",
      items: [
        { value: "98%", label: "Response rate", desc: "in under 30 seconds" },
        { value: "3.2x", label: "More appointments", desc: "booked automatically" },
        { value: "45%", label: "Fewer no-shows", desc: "with WhatsApp reminders" },
        { value: "24/7", label: "Availability", desc: "at no extra cost" }
      ]
    },
    positioning: {
      overline: "What Nexum is",
      title1: "More than software.",
      title2: "A real digital employee.",
      p1: "Nexum isn't a chatbot or a simple automation tool. It acts like a real team member integrated into your business — handling communication, appointments, reminders and follow-up with consistency and professionalism.",
      p2: "Available 24/7. Speaking multiple languages. Never missing a message. Always following up.",
      cards: [
        { label: "Multilingual", desc: "Speaks your customers' language" },
        { label: "24/7 Available", desc: "Never misses a message" },
        { label: "Auto-booking", desc: "Schedules appointments" },
        { label: "Follow-up", desc: "Nurtures relationships" }
      ]
    },
    industries: {
      overline: "Who is it for",
      title: "Designed for service businesses",
      items: ["Dental clinics", "Beauty centers", "Physiotherapists", "Auto workshops", "Hair salons", "Medical centers"]
    },
    problem: {
      overline: "The Problem",
      title: "Most businesses still lose money through poor customer handling.",
      subtitle: "Every missed call, slow reply, and forgotten follow-up is revenue walking out the door. The problem isn't effort — it's capacity.",
      items: [
        "Missed calls that never get returned",
        "Slow replies that lose customers",
        "Forgotten appointments and no-shows",
        "Clients who visit once and never return",
        "Inconsistent communication",
        "Language barriers with clients",
        "No one available outside business hours"
      ]
    },
    capabilities: {
      overline: "Capabilities",
      title: "What Nexum does",
      items: [
        { title: "Answers naturally", desc: "Intelligent conversations that feel human" },
        { title: "Speaks many languages", desc: "Communicates in the customer's preferred language" },
        { title: "Works 24/7", desc: "Always available, even when your team isn't" },
        { title: "Books appointments", desc: "Schedules meetings directly into your calendar" },
        { title: "Sends reminders", desc: "WhatsApp notifications that reduce no-shows" },
        { title: "Follows up", desc: "Automated check-ins that build loyalty" },
        { title: "Recovers clients", desc: "Re-engages customers who haven't returned" },
        { title: "Elevates your brand", desc: "Professional image through consistent communication" }
      ]
    },
    howItWorks: {
      overline: "Process",
      title: "How Nexum works inside your business",
      steps: [
        { num: "01", title: "Customer reaches out", desc: "A customer writes via WhatsApp, calls, or sends a message to your business." },
        { num: "02", title: "Nexum responds", desc: "AI understands the context, replies in the customer's language, and guides the conversation." },
        { num: "03", title: "You get results", desc: "Appointments booked, reminders scheduled, follow-ups automated. All without lifting a finger." }
      ]
    },
    testimonials: {
      overline: "Testimonials",
      title: "What our clients say",
      items: [
        { text: "Nexum has completely transformed how we manage appointments. We used to miss calls constantly, now every customer gets an immediate response.", author: "María García", role: "Director, Sonrisa Dental Clinic", rating: 5 },
        { text: "The automatic follow-up has been a game-changer. Our clients feel taken care of and the return rate has increased by 40%.", author: "Carlos Rodríguez", role: "Owner, AutoTaller Express", rating: 5 },
        { text: "The multilingual capability is incredible. We have clients from all over Europe and Nexum serves them in their language without any issues.", author: "Sophie Laurent", role: "Manager, Spa Wellness Center", rating: 5 }
      ]
    },
    dashboard: {
      overline: "Dashboard",
      title: "A system designed for real business operations",
      subtitle: "Monitor appointments, conversations, reminders and client activity in one premium interface.",
      stats: { appointments: "Today's Appointments", conversations: "Active Conversations", reminders: "Reminders Sent" },
      activity: "Recent Activity",
      actions: [
        { action: "Appointment booked", client: "María García", lang: "ES" },
        { action: "Reminder sent", client: "John Smith", lang: "EN" },
        { action: "Follow-up completed", client: "Pierre Dubois", lang: "FR" }
      ]
    },
    value: {
      overline: "Business Impact",
      title1: "A better customer experience.",
      title2: "A more modern business.",
      items: [
        { title: "Fewer lost opportunities", desc: "Every inquiry gets a professional response" },
        { title: "Better response times", desc: "Instant replies, any hour of the day" },
        { title: "More booked appointments", desc: "Automated scheduling increases conversions" },
        { title: "Stronger retention", desc: "Consistent follow-up builds loyalty" },
        { title: "More order and control", desc: "Everything tracked in one place" },
        { title: "Better service image", desc: "Modern, reliable communication" }
      ]
    },
    personalization: {
      overline: "Configuration",
      title: "Configured for your business.",
      subtitle: "Nexum adapts to how your business operates. Every response, every reminder, every follow-up is configured to match your specific needs.",
      configs: ["Operating schedules", "Services offered", "Business rules", "Team availability", "Preferred languages", "Communication tone", "Reminder timing", "Follow-up sequences"],
      preview: "Configuration Preview",
      previewItems: [
        { label: "Business type", value: "Dental Clinic" },
        { label: "Languages", value: "Spanish, English, French" },
        { label: "Response time", value: "< 30 seconds" },
        { label: "Reminders", value: "24h + 2h before" },
        { label: "Follow-up", value: "3 days after service" }
      ]
    },
    faq: {
      overline: "FAQ",
      title: "Everything you need to know",
      items: [
        { q: "How long does implementation take?", a: "Typical implementation takes 5-10 business days, including custom configuration, integration with your systems, and team training." },
        { q: "What languages does Nexum support?", a: "Nexum supports 20+ languages including Spanish, English, French, German, Italian, Portuguese, and many more. Languages can be configured based on your business needs." },
        { q: "Does it integrate with my current booking system?", a: "Yes, Nexum integrates with most appointment management systems, Google/Outlook calendars, and can work with your existing CRM." },
        { q: "What happens if a customer needs to speak with a person?", a: "Nexum automatically detects when a conversation requires human intervention and transfers the chat to your team with full conversation context." },
        { q: "Is there a minimum commitment?", a: "No lock-in contracts. You can cancel monthly. We believe the best way to retain you is with results, not contracts." }
      ]
    },
    pricing: {
      overline: "Investment",
      title: "Premium implementation for businesses that want real control.",
      subtitle: "This isn't a simple software subscription. It's a complete deployment of a digital employee — with dedicated onboarding, configuration, and ongoing optimization.",
      note: "One recovered client pays for your monthly service",
      implementation: "Implementation",
      implementationPrice: "€2,650",
      implementationNote: "one-time",
      implementationDesc: "Full setup, configuration, and training",
      monthly: "Monthly Service",
      monthlyPrice: "€169",
      monthlyNote: "/month",
      monthlyDesc: "Ongoing AI management and optimization",
      features: ["Custom AI configuration", "WhatsApp Business integration", "Appointment scheduling", "Multilingual support", "Follow-up automation", "Priority support", "Monthly reports"],
      cta: "Request a demo"
    },
    finalCta: {
      title: "Add Nexum to your business.",
      subtitle: "See how Nexum can work like a real member of your team.",
      cta: "Request demo"
    },
    footer: {
      tagline: "Digital Employee",
      copyright: "All rights reserved."
    }
  }
};

// Language Context
const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);

// ==================== MOBILE PERFORMANCE ====================
const _isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;
const _prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
const FAST_MOBILE = _isMobileDevice || _prefersReducedMotion;

// ==================== ANIMATION VARIANTS ====================
// On mobile: minimal animations (no blur, no scale, shorter durations) → saves ~60% GPU
const _ease = [0.16, 1, 0.3, 1];
const _d = FAST_MOBILE ? 0.3 : 0.6;
const _dLong = FAST_MOBILE ? 0.35 : 0.7;

const fadeIn = {
  hidden: { opacity: 0, y: FAST_MOBILE ? 12 : 30 },
  visible: { opacity: 1, y: 0, transition: { duration: _d, ease: _ease } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: FAST_MOBILE ? -15 : -40 },
  visible: { opacity: 1, x: 0, transition: { duration: _d, ease: _ease } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: FAST_MOBILE ? 15 : 40 },
  visible: { opacity: 1, x: 0, transition: { duration: _d, ease: _ease } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: FAST_MOBILE ? 0.95 : 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: _d, ease: _ease } }
};

const scaleFromCenter = {
  hidden: { opacity: 0, scale: FAST_MOBILE ? 0.95 : 0.7 },
  visible: { opacity: 1, scale: 1, transition: { duration: _dLong, ease: _ease } }
};

// Blur is extremely expensive on mobile GPU — skip it entirely
const blurIn = FAST_MOBILE
  ? { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: _ease } } }
  : { hidden: { opacity: 0, filter: "blur(12px)", y: 20, scale: 0.95 }, visible: { opacity: 1, filter: "blur(0px)", y: 0, scale: 1, transition: { duration: 0.7, ease: _ease } } };

const expandFromCenter = {
  hidden: { opacity: 0, scale: FAST_MOBILE ? 0.97 : 0.8, y: FAST_MOBILE ? 8 : 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: FAST_MOBILE ? 0.25 : 0.5, ease: _ease } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: FAST_MOBILE ? 0.04 : 0.1, delayChildren: FAST_MOBILE ? 0 : 0.05 } }
};

const staggerFast = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: FAST_MOBILE ? 0.02 : 0.06 } }
};

const staggerFromCenter = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: FAST_MOBILE ? 0.03 : 0.08, delayChildren: FAST_MOBILE ? 0 : 0.1 } }
};

// ==================== ANIMATED COUNTER ====================
const AnimatedCounter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      const num = parseFloat(value);
      const duration = 2000;
      const steps = 60;
      const increment = num / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= num) {
          setCount(num);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <span ref={ref}>
      {value.includes('.') ? count.toFixed(1) : Math.floor(count)}{suffix}
    </span>
  );
};

// ==================== HEADER ====================
const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [onLightBg, setOnLightBg] = useState(false);
  
  useEffect(() => {
    let rafId = 0;
    let cachedSections = null;
    let lastTick = 0;

    const handleScroll = () => {
      const now = performance.now();
      // Throttle: max 1 check every 100ms on mobile, 60ms on desktop
      if (now - lastTick < (FAST_MOBILE ? 100 : 60)) return;
      lastTick = now;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);

        const headerHeight = 70;
        const scrollPos = window.scrollY + headerHeight;

        // Cache light sections query (they don't change)
        if (!cachedSections) {
          cachedSections = document.querySelectorAll('section:not([data-theme="dark"])');
        }
        let isOnLight = false;
        for (let i = 0; i < cachedSections.length; i++) {
          const rect = cachedSections[i].getBoundingClientRect();
          const sectionTop = rect.top + window.scrollY;
          const sectionBottom = sectionTop + rect.height;
          if (scrollPos >= sectionTop && scrollPos <= sectionBottom) {
            isOnLight = true;
            break;
          }
        }
        setOnLightBg(isOnLight);
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => { window.removeEventListener('scroll', handleScroll); cancelAnimationFrame(rafId); };
  }, []);
  
  const textColor = onLightBg ? 'text-zinc-900' : 'text-white';
  const textColorMuted = onLightBg ? 'text-zinc-600' : 'text-white/70';
  const borderColor = onLightBg ? 'border-zinc-300' : 'border-white/20';
  
  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={scrolled ? {
        backgroundColor: 'hsla(0, 0%, 67%, 0.10)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: 'rgba(0, 0, 0, 0.10) 0px 2px 10px 0px'
      } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 md:py-4 flex items-center justify-between">
        <a href="/" className="flex items-center" data-testid="logo">
          <span className={`text-lg md:text-xl font-grotesk font-semibold tracking-tight transition-colors duration-300 ${textColor}`}>
            Nexum
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#capabilities" className={`text-sm transition-colors ${textColorMuted} hover:${textColor}`} data-testid="nav-capabilities">{t.nav.capabilities}</a>
          <a href="#how-it-works" className={`text-sm transition-colors ${textColorMuted} hover:${textColor}`} data-testid="nav-how">{t.nav.how}</a>
          <a href="#pricing" className={`text-sm transition-colors ${textColorMuted} hover:${textColor}`} data-testid="nav-pricing">{t.nav.pricing}</a>
        </nav>
        <div className="flex items-center gap-2 md:gap-4">
          <button 
            onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
            className={`px-2.5 md:px-3 py-1 md:py-1.5 text-[10px] md:text-xs font-medium transition-all duration-300 ${textColorMuted} border ${borderColor} rounded-full hover:opacity-80`}
            data-testid="lang-toggle"
          >
            {lang === 'es' ? 'EN' : 'ES'}
          </button>
          <a href={CALENDLY_URL} onClick={openCalendly} className={`hidden sm:block transition-all px-4 md:px-5 py-2 md:py-2.5 font-medium text-xs md:text-sm ${onLightBg ? 'bg-zinc-900 text-white hover:bg-zinc-800' : 'bg-white text-black hover:bg-white/90'}`} data-testid="header-cta">
            {t.nav.demo}
          </a>
        </div>
      </div>
    </header>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => {
  const { t, lang } = useLanguage();
  
  return (
    <section className="relative min-h-screen bg-[#0a0a0a] overflow-visible" data-testid="hero-section" data-theme="dark" style={{ paddingTop: 0, marginTop: 0 }}>
      {/* Full-screen background image - extends above to cover header area AND iOS safe area */}
      <div className="absolute left-0 right-0 bottom-0 z-0" style={{ top: 'calc(-100px - env(safe-area-inset-top, 0px))' }}>
        {/* Mobile image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/bzv22ar6_7C2D7290-895D-41E3-B525-6F85B277BCE9-2.png"
          alt="Nexum representative"
          className="sm:hidden w-full h-full object-cover object-center"
          data-testid="hero-image-mobile"
          fetchPriority="high"
          decoding="async"
          width="750"
          height="1334"
        />
        {/* Desktop image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/bkjvnv1y_7C2D7290-895D-41E3-B525-6F85B277BCE9.PNG"
          alt="Nexum representative"
          className="hidden sm:block w-full h-full object-cover object-top"
          data-testid="hero-image-desktop"
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="1080"
        />
        {/* Cinematic overlays - lighter to show the image better */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      </div>
      
      {/* Subtle animated gradient orb */}
      <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
      
      {/* Content */}
      <div className="relative z-10 min-h-[85vh] sm:min-h-screen flex items-center pt-14 sm:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-24 lg:py-32 grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Text content */}
          <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col min-h-[65vh] sm:min-h-0 justify-between sm:justify-start">
            {/* Title Section - arriba en móvil */}
            <div className="pt-44 sm:pt-0">
              <motion.h1 variants={fadeIn} className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-grotesk font-bold tracking-tight leading-[1.05] text-white mb-3 sm:mb-4 md:mb-6">
                {t.hero.title1}<br />
                {t.hero.title2}<br />
                <span className="bg-gradient-to-r from-white via-zinc-300 to-zinc-500 bg-clip-text text-transparent">{t.hero.title3}</span>
              </motion.h1>
              
              <motion.p variants={fadeIn} className="text-sm sm:text-base md:text-lg text-zinc-400 max-w-md">
                {lang === 'es' ? 'Agenda citas, responde llamadas y hace seguimiento. Automático.' : 'Books appointments, answers calls and follows up. Automatic.'}
              </motion.p>
            </div>
            
            {/* CTAs y Stats - abajo en móvil */}
            <div className="mt-auto sm:mt-8 md:mt-10">
              <motion.div variants={fadeIn} className="flex flex-row gap-2 sm:gap-3 md:gap-4">
                <a href={CALENDLY_URL} onClick={openCalendly} className="group inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-black hover:bg-zinc-100 transition-all px-4 sm:px-6 md:px-10 py-2.5 sm:py-3.5 md:py-4 font-semibold text-xs sm:text-sm shadow-lg shadow-white/10" data-testid="hero-cta-primary">
                  {t.hero.cta1}
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
              
              {/* Minimal stats row */}
              <motion.div 
                variants={fadeIn} 
                className="mt-4 sm:mt-8 md:mt-12 pt-4 sm:pt-6 md:pt-8 border-t border-white/10 flex flex-wrap items-center gap-4 sm:gap-6 md:gap-12"
              >
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-grotesk font-bold text-white">98%</p>
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider mt-0.5 sm:mt-1">Respuesta</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-grotesk font-bold text-white">24/7</p>
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider mt-0.5 sm:mt-1">Disponible</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-grotesk font-bold text-white">+20</p>
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider mt-0.5 sm:mt-1">Idiomas</p>
                </div>
                
                {/* GDPR EU Badge - 12 stars circle with centered text */}
                <div className="ml-auto opacity-70 hover:opacity-100 transition-opacity">
                  <svg width="55" height="55" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* 12 stars in perfect circle around center point (27.5, 27.5), radius 20 */}
                    <g fill="white" opacity="0.85">
                      <polygon points="27.5,7.5 28.3,10 30.5,10 28.6,11.5 29.4,14 27.5,12.5 25.6,14 26.4,11.5 24.5,10 26.7,10"/>
                      <polygon points="37.8,10.2 38.6,12.7 40.8,12.7 38.9,14.2 39.7,16.7 37.8,15.2 35.9,16.7 36.7,14.2 34.8,12.7 37,12.7"/>
                      <polygon points="44.8,17.2 45.6,19.7 47.8,19.7 45.9,21.2 46.7,23.7 44.8,22.2 42.9,23.7 43.7,21.2 41.8,19.7 44,19.7"/>
                      <polygon points="47.5,27.5 48.3,30 50.5,30 48.6,31.5 49.4,34 47.5,32.5 45.6,34 46.4,31.5 44.5,30 46.7,30"/>
                      <polygon points="44.8,37.8 45.6,40.3 47.8,40.3 45.9,41.8 46.7,44.3 44.8,42.8 42.9,44.3 43.7,41.8 41.8,40.3 44,40.3"/>
                      <polygon points="37.8,44.8 38.6,47.3 40.8,47.3 38.9,48.8 39.7,51.3 37.8,49.8 35.9,51.3 36.7,48.8 34.8,47.3 37,47.3"/>
                      <polygon points="27.5,47.5 28.3,50 30.5,50 28.6,51.5 29.4,54 27.5,52.5 25.6,54 26.4,51.5 24.5,50 26.7,50"/>
                      <polygon points="17.2,44.8 18,47.3 20.2,47.3 18.3,48.8 19.1,51.3 17.2,49.8 15.3,51.3 16.1,48.8 14.2,47.3 16.4,47.3"/>
                      <polygon points="10.2,37.8 11,40.3 13.2,40.3 11.3,41.8 12.1,44.3 10.2,42.8 8.3,44.3 9.1,41.8 7.2,40.3 9.4,40.3"/>
                      <polygon points="7.5,27.5 8.3,30 10.5,30 8.6,31.5 9.4,34 7.5,32.5 5.6,34 6.4,31.5 4.5,30 6.7,30"/>
                      <polygon points="10.2,17.2 11,19.7 13.2,19.7 11.3,21.2 12.1,23.7 10.2,22.2 8.3,23.7 9.1,21.2 7.2,19.7 9.4,19.7"/>
                      <polygon points="17.2,10.2 18,12.7 20.2,12.7 18.3,14.2 19.1,16.7 17.2,15.2 15.3,16.7 16.1,14.2 14.2,12.7 16.4,12.7"/>
                    </g>
                    {/* GDPR text - perfectly centered */}
                    <text x="27.5" y="30.5" textAnchor="middle" fill="white" fontSize="8" fontWeight="500" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.8" opacity="0.9">GDPR</text>
                  </svg>
                </div>
              </motion.div>
            </div>
          </motion.div>
          
          {/* Right: Empty space for the image to show through */}
          <div className="hidden lg:block" />
        </div>
      </div>
      
      {/* Bottom gradient fade - seamless transition to stats */}
      <div className="absolute bottom-0 left-0 right-0 h-32 sm:h-48 bg-gradient-to-t from-black to-transparent z-10" />
    </section>
  );
};

// ==================== STATS SECTION ====================
const StatsSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-10 sm:py-12 md:py-16 bg-black" data-theme="dark">
      <div className="max-w-[320px] sm:max-w-2xl md:max-w-7xl mx-auto px-2 sm:px-6 md:px-12">
        <div className="grid grid-cols-4 gap-1 sm:gap-4 md:gap-8">
          {t.stats.items.map((stat, idx) => (
            <motion.div 
              key={`stat-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="text-center"
            >
              <p className="text-base sm:text-2xl md:text-4xl lg:text-5xl font-grotesk font-bold text-white mb-0.5 sm:mb-1 md:mb-2">
                {stat.value.includes('x') ? (
                  <><AnimatedCounter value={stat.value.replace('x', '')} />x</>
                ) : stat.value.includes('%') ? (
                  <><AnimatedCounter value={stat.value.replace('%', '')} />%</>
                ) : (
                  stat.value
                )}
              </p>
              <p className="text-[8px] sm:text-xs md:text-sm font-medium text-white mb-0.5 sm:mb-1 leading-tight">{stat.label}</p>
              <p className="text-[8px] sm:text-[10px] md:text-xs text-zinc-500 hidden sm:block">{stat.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== LOGOS MARQUEE ====================
const LogosMarquee = () => {
  const businesses = [
    { name: 'Audi Center Madrid', logo: 'AUDI CENTER', style: 'font-grotesk font-light tracking-[0.2em] uppercase' },
    { name: 'BMW Premium', logo: 'BMW PREMIUM', style: 'font-grotesk font-bold tracking-widest uppercase' },
    { name: 'Mercedes-Benz Select', logo: 'MB SELECT', style: 'font-grotesk font-medium tracking-wide' },
    { name: 'Porsche Experience', logo: 'PORSCHE', style: 'font-grotesk font-black tracking-tight uppercase' },
    { name: 'Lexus Exclusive', logo: 'LEXUS', style: 'font-light tracking-[0.3em] uppercase' },
    { name: 'Volvo Premium', logo: 'VOLVO', style: 'font-grotesk font-semibold tracking-widest uppercase' }
  ];
  
  return (
    <div className="py-8 sm:py-14 bg-light overflow-hidden border-y border-zinc-100">
      <div className="flex animate-marquee whitespace-nowrap">
        {[...Array(3)].map((_, i) => (
          <div key={`logos-${i}`} className="flex items-center gap-8 sm:gap-14 md:gap-20 mx-4 sm:mx-8 md:mx-10">
            {businesses.map((biz, j) => (
              <div key={`logo-${i}-${j}`} className={`text-zinc-400 hover:text-zinc-600 transition-colors cursor-default text-[10px] sm:text-xs md:text-base ${biz.style}`}>
                {biz.logo}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== PILOT BANNER ====================
const PilotBanner = () => {
  const { lang } = useLanguage();
  
  return (
    <div className="py-2 sm:py-4 bg-[#fafafa]">
      <div 
        className="relative max-w-4xl mx-auto overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 8px 40px rgba(0,0,0,0.1)'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/20" />
        
        <div className="relative z-10 px-4 sm:px-6 py-2 sm:py-4">
          <motion.a
            href={CALENDLY_URL} onClick={openCalendly}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="group flex flex-row items-center justify-center gap-2 sm:gap-6"
          >
            {/* Nexum logo */}
            <span className="text-xs sm:text-sm font-grotesk font-semibold text-white tracking-tight">
              Nexum
            </span>
            
            {/* Text */}
            <p className="text-[10px] sm:text-base text-white text-center font-medium">
              {lang === 'es' 
                ? 'Únete a pruebas piloto' 
                : 'Join pilot tests'}
              <span className="text-emerald-400 font-semibold hidden sm:inline"> — {lang === 'es' ? 'totalmente gratis' : 'completely free'}</span>
            </p>
            
            {/* CTA */}
            <span className="flex items-center gap-1 text-[10px] sm:text-sm font-medium text-white/80 group-hover:text-white transition-colors">
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </motion.a>
        </div>
      </div>
      
      {/* Texto de plazas limitadas - oculto en móvil */}
      <p className="hidden sm:block text-center text-[11px] text-zinc-400 mt-3 tracking-wide">
        {lang === 'es' 
          ? 'Plazas limitadas — Acceso ilimitado para negocios piloto'
          : 'Limited spots — Unlimited access for pilot businesses'}
      </p>
    </div>
  );
};

// ==================== PILOT TICKER ====================
const PilotTicker = () => {
  const message = "Plazas limitadas — Acceso ilimitado para negocios piloto";
  
  return (
    <div className="py-2 bg-[#fafafa] overflow-hidden border-b border-zinc-100">
      <div className="flex animate-marquee-slow whitespace-nowrap">
        {[...Array(8)].map((_, i) => (
          <span key={i} className="mx-8 text-[11px] text-zinc-400 tracking-wide">
            {message} <span className="mx-4 text-zinc-300">•</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ==================== POSITIONING SECTION ====================
// Scroll Text Reveal Component - Palantir Style
const ScrollTextReveal = ({ text, highlightWords = [] }) => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate progress based on element position
      const start = windowHeight * 0.9;
      const end = windowHeight * 0.2;
      
      if (rect.top > start) {
        setProgress(0);
      } else if (rect.top < end) {
        setProgress(1);
      } else {
        const p = (start - rect.top) / (start - end);
        setProgress(Math.min(1, Math.max(0, p)));
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const words = text.split(' ');
  
  // Line height: starts very expanded (2.5), ends normal (1.2)
  const lineHeight = 2.5 - (progress * 1.3); // From 2.5 to 1.2
  // Opacity: starts transparent, ends opaque
  const opacity = 0.15 + (progress * 0.85); // From 0.15 to 1
  
  return (
    <span 
      ref={containerRef}
      style={{
        lineHeight: lineHeight,
        display: 'block',
        transition: 'line-height 0.05s linear'
      }}
    >
      {words.map((word, idx) => {
        const isHighlight = highlightWords.some(hw => word.toLowerCase().includes(hw.toLowerCase().replace(/[,.]/, '')));
        
        // Highlighted words are gray and medium weight, normal words are black
        const finalColor = isHighlight ? '#71717a' : '#18181b';
        const fontWeight = isHighlight ? 500 : 400;
        
        return (
          <span
            key={idx}
            style={{
              color: finalColor,
              opacity: opacity,
              fontWeight: fontWeight,
              transition: 'opacity 0.05s linear'
            }}
          >
            {word}{' '}
          </span>
        );
      })}
    </span>
  );
};

const PositioningSection = () => {
  const { t, lang } = useLanguage();
  const icons = [Globe, Clock, Calendar, RefreshCw];
  
  // Colores variados para iconos
  const iconColors = [
    { bg: "bg-blue-50", text: "text-blue-600" },
    { bg: "bg-violet-50", text: "text-violet-600" },
    { bg: "bg-amber-50", text: "text-amber-600" },
    { bg: "bg-emerald-50", text: "text-emerald-600" },
  ];
  
  return (
    <section className="py-12 md:py-20 lg:py-28 bg-[#fafafa]" data-testid="positioning-section">
      <div className="max-w-6xl mx-auto px-5 md:px-12 lg:px-16">
        {/* Premium Verticals - Apple/LV/Gucci Luxury Style */}
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, margin: "-50px", amount: 0.15 }} 
          variants={stagger}
        >
          {/* Editorial Header - Luxury minimal */}
          <motion.div variants={blurIn} className="mb-16 md:mb-24">
            <span className="text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-zinc-400 mb-8 block">
              {lang === 'es' ? 'Sectores' : 'Sectors'}
            </span>
            <h3 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-medium text-zinc-900 leading-[1.1] tracking-tight max-w-4xl">
              {lang === 'es' 
                ? 'Atención que anticipa.' 
                : 'Attention that anticipates.'}
            </h3>
            <p className="text-base md:text-lg text-zinc-500 font-grotesk mt-6 max-w-2xl">
              {lang === 'es' 
                ? 'IA con voz natural. Disponible 24/7. Seguimiento proactivo de cada cliente.'  
                : 'AI with natural voice. Available 24/7. Proactive tracking of every client.'}
            </p>
          </motion.div>

          {/* Luxury Bento Grid - Horizontal en todos los tamaños */}
          <motion.div variants={staggerFast} className="grid grid-cols-12 gap-2 sm:gap-3 md:gap-4">
            
            {/* HERO Card - Concesionarios - Dominates left side */}
            <motion.div 
              variants={scaleIn}
              className="relative group overflow-hidden col-span-8 row-span-2"
            >
              <div className="relative aspect-[4/5] sm:aspect-[16/10] md:aspect-auto md:h-full md:min-h-[500px] lg:min-h-[600px]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/p7eqavqt_IMG_1790.jpg"
                  alt="Premium Automotive"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 group-hover:scale-[1.03]"
                />
                {/* Subtle gradient - more refined */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                
                {/* Content - Editorial style positioning */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-12 lg:p-16">
                  <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-4 md:mb-6">
                    <div className="w-4 sm:w-8 h-px bg-white/40" />
                    <span className="text-[8px] sm:text-[10px] font-medium tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/60">
                      {lang === 'es' ? 'Concesionarios' : 'Dealerships'}
                    </span>
                  </div>
                  <h4 className="text-lg sm:text-2xl md:text-4xl lg:text-5xl font-grotesk font-medium text-white mb-1 sm:mb-2 md:mb-4 tracking-tight leading-[1.1]">
                    {lang === 'es' ? 'Cada cliente, atendido.' : 'Every client, attended.'}
                  </h4>
                  <p className="text-[10px] sm:text-xs md:text-base text-white/60 font-grotesk leading-relaxed max-w-md hidden sm:block">
                    {lang === 'es' 
                      ? 'Agenda citas por voz. Avisa de revisiones. Recupera clientes inactivos.' 
                      : 'Book appointments by voice. Alert for services. Recover inactive clients.'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Top Right Card - Recambios / Servicio Técnico */}
            <motion.div 
              variants={scaleIn}
              className="relative group overflow-hidden col-span-4"
            >
              <div className="relative aspect-[3/4] sm:aspect-[4/3] md:h-full">
                <img 
                  src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/gx4nykji_IMG_1797.jpg"
                  alt="Technical Service"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-8">
                  <span className="text-[7px] sm:text-[9px] font-medium tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/50 mb-1 sm:mb-3 block">
                    {lang === 'es' ? 'Flotas' : 'Fleets'}
                  </span>
                  <h4 className="text-xs sm:text-base md:text-2xl font-grotesk font-medium text-white tracking-tight leading-tight mb-1 sm:mb-2">
                    {lang === 'es' ? 'Seguimiento proactivo.' : 'Proactive follow-up.'}
                  </h4>
                  <p className="text-[9px] sm:text-xs text-white/50 font-grotesk hidden md:block">
                    {lang === 'es' 
                      ? 'Avisa cuando toca el cambio de aceite, frenos o revisión.' 
                      : 'Alerts when oil change, brakes or service is due.'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bottom Right Card - Náutica */}
            <motion.div 
              variants={scaleIn}
              className="relative group overflow-hidden col-span-4"
            >
              <div className="relative aspect-[3/4] sm:aspect-[4/3] md:h-full">
                <img 
                  src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/onf7aley_2DDE1D6C-6BBB-49FE-9CBA-A199CD848132-2.png"
                  alt="Nautical"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-8">
                  <span className="text-[7px] sm:text-[9px] font-medium tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/50 mb-1 sm:mb-3 block">
                    {lang === 'es' ? 'Náutica' : 'Nautical'}
                  </span>
                  <h4 className="text-xs sm:text-base md:text-2xl font-grotesk font-medium text-white tracking-tight leading-tight mb-1 sm:mb-2">
                    {lang === 'es' ? 'Voz natural, 24/7.' : 'Natural voice, 24/7.'}
                  </h4>
                  <p className="text-[9px] sm:text-xs text-white/50 font-grotesk hidden md:block">
                    {lang === 'es' 
                      ? 'Gestiona reservas, mantenimientos y temporadas altas.' 
                      : 'Manage bookings, maintenance and peak seasons.'}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Minimal Tags - Refined */}
          <motion.div variants={fadeIn} className="flex flex-wrap justify-start gap-x-8 gap-y-3 pt-12 md:pt-16">
            {[
              lang === 'es' ? 'Concesionarios' : 'Dealerships',
              lang === 'es' ? 'Náutica' : 'Nautical',
              lang === 'es' ? 'Flotas' : 'Fleets',
              lang === 'es' ? 'Talleres' : 'Workshops'
            ].map((tag, idx) => (
              <span 
                key={idx}
                className="text-[11px] font-grotesk tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-600 transition-colors cursor-default"
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== INDUSTRIES SECTION ====================
// ==================== INDUSTRIES SECTION (REMOVED - unused, video deleted per user request) ====================

// ==================== PROACTIVE INTELLIGENCE SECTION ====================
const ProactiveIntelligenceSection = () => {
  const { lang } = useLanguage();
  
  const features = [
    {
      icon: Settings,
      title: lang === 'es' ? 'Sabe cuándo toca' : 'Knows when it\'s due',
      desc: lang === 'es' 
        ? 'Distribución a 120.000km. Frenos a 40.000km. Aceite cada 15.000km. Nexum lo sabe y avisa.'
        : 'Timing belt at 120,000km. Brakes at 40,000km. Oil every 15,000km. Nexum knows and alerts.',
      stat: '120K',
      statLabel: 'km'
    },
    {
      icon: Bell,
      title: lang === 'es' ? 'Avisa antes de que llamen' : 'Alerts before they call',
      desc: lang === 'es'
        ? 'WhatsApp automático al cliente: "Tu BMW X3 tiene 38.500km. ¿Agendamos revisión de frenos?"'
        : 'Automatic WhatsApp to client: "Your BMW X3 has 38,500km. Shall we schedule brake check?"',
      stat: '48h',
      statLabel: lang === 'es' ? 'antes' : 'ahead'
    },
    {
      icon: TrendingUp,
      title: lang === 'es' ? 'Recupera clientes dormidos' : 'Recovers dormant clients',
      desc: lang === 'es'
        ? 'Nexum reactiva la relación con seguimiento personalizado y recordatorios inteligentes.'
        : 'Nexum reactivates the relationship with personalized follow-up and smart reminders.',
      stat: '+40%',
      statLabel: lang === 'es' ? 'retorno' : 'return'
    }
  ];
  
  return (
    <section className="relative py-24 md:py-40 overflow-hidden bg-black" data-theme="dark" data-testid="proactive-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1606577924006-27d39b132ae2?w=1920&q=80"
          alt="Premium automotive"
          className="w-full h-full object-cover opacity-30"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
      </div>
      
      {/* Glow */}
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[200px] -translate-y-1/2" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24 max-w-3xl"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-500 mb-6 block">
            {lang === 'es' ? 'Inteligencia Proactiva' : 'Proactive Intelligence'}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-grotesk font-semibold tracking-tight text-white leading-[1.05] mb-6">
            {lang === 'es' ? 'Anticipa.' : 'Anticipate.'}
            <br />
            <span className="text-zinc-900">{lang === 'es' ? 'No reacciones.' : 'Don\'t react.'}</span>
          </h2>
          <p className="text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
            {lang === 'es' 
              ? 'Nexum conoce el historial de cada vehículo. Sabe cuándo toca mantenimiento y contacta al cliente antes de que él te llame.'
              : 'Nexum knows each vehicle\'s history. Knows when maintenance is due and contacts the client before they call you.'}
          </p>
        </motion.div>
        
        {/* Features Grid */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="grid md:grid-cols-3 gap-4 md:gap-6"
        >
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={`proactive-${idx}`}
                variants={fadeIn}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group relative bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 p-6 md:p-8 hover:border-zinc-700 transition-all duration-500"
              >
                {/* Stat */}
                <div className="absolute top-6 right-6 text-right">
                  <span className="text-3xl md:text-4xl font-grotesk font-bold text-white/10 group-hover:text-white/20 transition-colors">
                    {feature.stat}
                  </span>
                  <span className="block text-[10px] text-zinc-600 uppercase tracking-wider">
                    {feature.statLabel}
                  </span>
                </div>
                
                {/* Icon */}
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mb-6 group-hover:bg-zinc-700 transition-colors">
                  <Icon className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                
                {/* Content */}
                <h3 className="text-lg md:text-xl font-grotesk font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== ENTERPRISE SECURITY SECTION ====================
// ==================== LOCK ANIMATION COMPONENT (for inside section) ====================
const LockAnimationInSection = () => {
  const { lang } = useLanguage();
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const animRef = useRef(null);
  const scrollSectionRef = useRef(null);
  const smoothTotal = useRef(0);
  const rafRef = useRef(0);
  const [phase2, setPhase2] = useState(0);
  const [lockProgress, setLockProgress] = useState(0);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  // Initialize Lottie (bundled via npm, no CDN dependency)
  useEffect(() => {
    if (!containerRef.current) return;
    animRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "html",
      loop: false,
      autoplay: false,
      animationData: LOCK_DATA,
    });
    return () => animRef.current?.destroy();
  }, []);

  // Scroll animation
  useEffect(() => {
    const getTarget = () => {
      const section = scrollSectionRef.current;
      if (!section) return 0;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight - window.innerHeight;
      return sectionHeight > 0 ? Math.min(Math.max(-rect.top / sectionHeight, 0), 1) : 0;
    };
    
    const tick = () => {
      smoothTotal.current = lerp(smoothTotal.current, getTarget(), 0.22);
      const total = smoothTotal.current;
      const p1 = Math.min(total / 0.5, 1);
      const p2 = Math.min(Math.max((total - 0.5) / 0.3, 0), 1);
      
      animRef.current?.goToAndStop(25 - p1 * 20, true);
      
      // Hide first child at start
      if (containerRef.current && containerRef.current.children[0]) {
        containerRef.current.children[0].style.opacity = p1 < 0.9 ? '0' : '1';
      }
      
      setLockProgress(p1);
      
      if (wrapperRef.current) {
        wrapperRef.current.style.transform = `translateY(${(1 - p1) * 120}px)`;
        wrapperRef.current.style.opacity = String(p1 < 0.2 ? (p1 / 0.2) * 0.3 : 1);
      }
      setPhase2(p2);
      rafRef.current = requestAnimationFrame(tick);
    };
    
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: lang === 'es' ? 'Preparación ISO 27001' : 'ISO 27001 Preparation',
      desc: lang === 'es' 
        ? 'Arquitectura y procesos estructurados siguiendo este estándar internacional.'
        : 'Architecture and processes structured following this international standard.'
    },
    {
      icon: Target,
      title: lang === 'es' ? 'Evaluaciones planificadas' : 'Planned Assessments',
      desc: lang === 'es'
        ? 'Revisiones técnicas y pruebas de seguridad periódicas en nuestra hoja de ruta.'
        : 'Technical reviews and periodic security tests in our roadmap.'
    },
    {
      icon: Server,
      title: lang === 'es' ? 'Privacidad RGPD' : 'GDPR Privacy',
      desc: lang === 'es'
        ? 'Tratamiento de datos bajo principios de minimización y almacenamiento europeo.'
        : 'Data processing under minimization principles and European storage.'
    },
    {
      icon: Lock,
      title: lang === 'es' ? 'Cumplimiento en desarrollo' : 'Compliance in Development',
      desc: lang === 'es'
        ? 'Evaluando proveedores y protocolos para entornos empresariales.'
        : 'Evaluating providers and protocols for enterprise environments.'
    }
  ];

  return (
    <div 
      ref={scrollSectionRef} 
      style={{ height: "500vh", background: "transparent", marginTop: "-500vh", position: "relative", zIndex: 20, paddingTop: "50vh" }}
      data-testid="lock-scroll-section"
    >
      <div style={{ position: "sticky", top: 0, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
        {/* Lock Container - responsive size */}
        <div className="w-[70px] h-[70px] sm:w-[80px] sm:h-[80px] md:w-[90px] md:h-[90px]" style={{ position: "relative", marginBottom: "0.5rem" }}>
          <div 
            ref={containerRef}
            style={{ 
              width: "100%", 
              height: "100%"
            }}
          />
        </div>
        
        {/* Text Wrapper */}
        <div 
          ref={wrapperRef}
          style={{ 
            position: "relative", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            transform: "translateY(120px)",
            opacity: 0,
            marginBottom: "1rem"
          }}
        >
          <h1 
            className="font-grotesk text-center px-4"
            style={{ 
              color: "#000", 
              fontSize: "clamp(1.1rem, 4vw, 2.2rem)", 
              fontWeight: 500, 
              letterSpacing: "-0.02em",
              margin: 0, 
              opacity: Math.max(1 - phase2 * 3, 0),
              lineHeight: 1.2
            }}
          >
            {lang === 'es' ? 'Preparado para los estándares más exigentes' : 'Ready for the most demanding standards'}
          </h1>
          
          {/* Dots */}
          <div style={{ 
            position: "absolute", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: 14 + (1 - phase2) * 46,
            pointerEvents: "none" 
          }}>
            {Array.from({ length: 8 }).map((_, i) => {
              const p = Math.min(Math.max((phase2 - i * 0.1) / 0.15, 0), 1);
              return (
                <div 
                  key={i} 
                  style={{ 
                    width: 13, 
                    height: 13, 
                    borderRadius: "50%", 
                    background: "#000", 
                    opacity: p, 
                    transform: `scale(${0.2 + p * 0.8})`
                  }} 
                />
              );
            })}
          </div>
        </div>

        {/* Security Cards - Below the lock */}
        <div 
          style={{ 
            opacity: Math.min(Math.max((phase2 - 0.3) * 3, 0), 1),
            transform: `translateY(${(1 - Math.min(Math.max((phase2 - 0.3) * 3, 0), 1)) * 30}px)`,
            transition: "opacity 0.3s, transform 0.3s",
            width: "100%",
            maxWidth: "1200px",
            padding: "0 1rem",
            marginTop: "0"
          }}
        >
          {/* Cards Grid - 4 horizontal */}
          <div style={{ 
            display: "flex", 
            flexDirection: "row",
            gap: "0.75rem",
            width: "100%",
            marginBottom: "4rem"
          }}>
            {securityFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={idx}
                  style={{
                    backgroundColor: 'hsla(0, 0%, 67%, 0.15)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    padding: '1.25rem 1rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px 0px',
                    flex: 1
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(255,255,255,0.5)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.75rem',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <Icon style={{ width: '24px', height: '24px', color: '#000' }} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-grotesk" style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: 600, 
                    color: '#000',
                    margin: 0,
                    lineHeight: 1.3,
                    textAlign: 'center'
                  }}>
                    {feature.title}
                  </h3>
                </div>
              );
            })}
          </div>

          {/* Section Header - Below cards */}
          <div style={{ textAlign: "center" }}>
            <span style={{ 
              fontSize: "11px", 
              fontWeight: 500, 
              letterSpacing: "0.2em", 
              textTransform: "uppercase", 
              color: "#000",
              display: "block",
              marginBottom: "0.75rem"
            }}>
              {lang === 'es' ? 'Infraestructura de Confianza' : 'Trust Infrastructure'}
            </span>
            <h2 className="font-grotesk" style={{ 
              fontSize: "clamp(1.5rem, 3vw, 2rem)", 
              fontWeight: 600, 
              color: "#000",
              margin: 0,
              lineHeight: 1.2
            }}>
              {lang === 'es' ? 'La confianza no se añade al final.' : 'Trust is not added at the end.'}
              <br />
              <span style={{ color: "#000", opacity: 0.6 }}>{lang === 'es' ? 'Se diseña desde el principio.' : 'It\'s designed from the start.'}</span>
            </h2>
            
            {/* Expandable text */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: isTextExpanded ? 'auto' : 0, opacity: isTextExpanded ? 1 : 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <p style={{ 
                fontSize: '0.9rem', 
                color: '#000',
                opacity: 0.7,
                maxWidth: '600px',
                margin: '1rem auto 0',
                lineHeight: 1.6
              }}>
                {lang === 'es' 
                  ? 'Nexum está construyendo su infraestructura con criterios de privacidad, control y seguridad orientados a entornos empresariales. Cada capa se diseña pensando en la protección de datos y en una futura validación mediante estándares reconocidos.'
                  : 'Nexum is building its infrastructure with privacy, control and security criteria oriented to enterprise environments. Every layer is designed with data protection in mind and future validation through recognized standards.'}
              </p>
            </motion.div>
            
            {/* Expand button */}
            <button
              onClick={() => setIsTextExpanded(!isTextExpanded)}
              style={{
                marginTop: '1rem',
                fontSize: '0.75rem',
                color: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginLeft: 'auto',
                marginRight: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                paddingBottom: '3rem'
              }}
            >
              {isTextExpanded 
                ? (lang === 'es' ? 'Ocultar' : 'Hide') 
                : (lang === 'es' ? 'Saber más' : 'Learn more')}
              <ChevronDown style={{ width: '12px', height: '12px', transition: 'transform 0.3s', transform: isTextExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnterpriseSecuritySection = () => {
  const { lang } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: lang === 'es' ? 'ISO 27001' : 'ISO 27001',
      desc: lang === 'es' 
        ? 'Arquitectura estructurada bajo estándares internacionales de seguridad.'
        : 'Architecture structured under international security standards.'
    },
    {
      icon: Target,
      title: lang === 'es' ? 'Evaluaciones periódicas' : 'Periodic Assessments',
      desc: lang === 'es'
        ? 'Revisiones técnicas y pruebas de seguridad continuas.'
        : 'Technical reviews and continuous security testing.'
    },
    {
      icon: Server,
      title: lang === 'es' ? 'RGPD' : 'GDPR',
      desc: lang === 'es'
        ? 'Datos tratados bajo principios europeos de privacidad.'
        : 'Data processed under European privacy principles.'
    },
    {
      icon: Lock,
      title: lang === 'es' ? 'Cumplimiento' : 'Compliance',
      desc: lang === 'es'
        ? 'Protocolos y coberturas para entornos empresariales.'
        : 'Protocols and coverage for enterprise environments.'
    }
  ];
  
  return (
    <section className="relative py-16 md:py-24 overflow-hidden" data-testid="security-section">
      {/* Same cloud background as VoiceDemo */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/o5ndff8m_EA7CADF3-1201-431A-8440-5F5DEAD73B6D%202-2.png)' }}
      />
      <div className="absolute inset-0 bg-white/40" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-12 lg:px-20">
        <div className="flex flex-col items-center text-center">
          {/* Text */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-900 mb-4 block">
              {lang === 'es' ? 'Infraestructura de Confianza' : 'Trust Infrastructure'}
            </span>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-grotesk font-semibold tracking-tight text-zinc-900 leading-[1.1] mb-4">
              {lang === 'es' ? 'La confianza no se añade al final.' : 'Trust is not added at the end.'}
              <br />
              <span className="text-zinc-500">{lang === 'es' ? 'Se diseña desde el principio.' : 'It\'s designed from the start.'}</span>
            </h2>
            
            {/* Expandable text */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <p className="text-sm text-zinc-600 max-w-lg mx-auto leading-relaxed pt-4 pb-2">
                {lang === 'es' 
                  ? 'Nexum está construyendo su infraestructura con criterios de privacidad, control y seguridad orientados a entornos empresariales. Cada capa se diseña pensando en la protección de datos y en una futura validación mediante estándares reconocidos.'
                  : 'Nexum is building its infrastructure with privacy, control and security criteria oriented to enterprise environments. Every layer is designed with data protection in mind and future validation through recognized standards.'}
              </p>
            </motion.div>
            
            {/* Expand button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-3 text-xs text-zinc-500 hover:text-zinc-800 transition-colors flex items-center gap-1 mx-auto"
            >
              {isExpanded 
                ? (lang === 'es' ? 'Ocultar' : 'Hide') 
                : (lang === 'es' ? 'Saber más' : 'Learn more')}
              <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </motion.div>
          
          {/* Cards - 4 in a row horizontal */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="flex flex-row gap-2 w-full"
          >
            {securityFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={`security-${idx}`}
                  variants={fadeIn}
                  className="group bg-white/70 backdrop-blur-md border border-zinc-200/60 p-2 flex-1"
                >
                  <div className="w-6 h-6 bg-zinc-100/80 flex items-center justify-center mb-1.5">
                    <Icon className="w-3 h-3 text-zinc-700" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-[9px] md:text-[10px] font-grotesk font-semibold text-zinc-900 leading-tight">
                    {feature.title}
                  </h3>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== PREMIUM PROCESS SECTION ====================
const PremiumProcessSection = () => {
  const { lang } = useLanguage();
  
  const steps = [
    {
      num: '01',
      title: lang === 'es' ? 'Conectamos' : 'We connect',
      desc: lang === 'es' 
        ? 'Integramos Nexum con tu DMS, agenda y WhatsApp Business en menos de 48 horas.'
        : 'We integrate Nexum with your DMS, calendar and WhatsApp Business in under 48 hours.',
      icon: Settings
    },
    {
      num: '02', 
      title: lang === 'es' ? 'Entrenamos' : 'We train',
      desc: lang === 'es'
        ? 'Configuramos respuestas, tono de marca y lógica de mantenimientos específica para tu taller.'
        : 'We configure responses, brand tone and maintenance logic specific to your workshop.',
      icon: Zap
    },
    {
      num: '03',
      title: lang === 'es' ? 'Activamos' : 'We activate', 
      desc: lang === 'es'
        ? 'Nexum empieza a atender, agendar y hacer seguimiento. Tú te enfocas en el trabajo técnico.'
        : 'Nexum starts answering, scheduling and following up. You focus on the technical work.',
      icon: Check
    }
  ];
  
  return (
    <section id="how-it-works" className="relative py-24 md:py-36 bg-[#fafafa]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-400 mb-6 block">
            {lang === 'es' ? 'Proceso' : 'Process'}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-semibold tracking-tight text-zinc-900">
            {lang === 'es' ? 'Implementación en 3 pasos' : '3-step implementation'}
          </h2>
        </motion.div>
        
        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-24 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-300 to-transparent" />
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-8 md:gap-12"
          >
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={`step-${idx}`}
                  variants={fadeIn}
                  className="relative text-center"
                >
                  {/* Number Circle */}
                  <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24 rounded-full bg-white border-2 border-zinc-200 flex items-center justify-center mb-8 shadow-lg shadow-zinc-200/50">
                    <span className="text-2xl md:text-3xl font-grotesk font-bold text-zinc-900">{step.num}</span>
                    {/* Icon Badge */}
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-grotesk font-semibold text-zinc-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-zinc-500 leading-relaxed max-w-xs mx-auto">
                    {step.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== RESULTS SECTION ====================
const ResultsSection = () => {
  const { lang } = useLanguage();
  
  const results = [
    { value: '98%', label: lang === 'es' ? 'Llamadas atendidas' : 'Calls answered', desc: lang === 'es' ? 'vs 60% media del sector' : 'vs 60% industry average' },
    { value: '3.2x', label: lang === 'es' ? 'Más citas' : 'More appointments', desc: lang === 'es' ? 'agendadas automáticamente' : 'booked automatically' },
    { value: '-45%', label: lang === 'es' ? 'No-shows' : 'No-shows', desc: lang === 'es' ? 'con recordatorios WhatsApp' : 'with WhatsApp reminders' },
    { value: '+40%', label: lang === 'es' ? 'Clientes recurrentes' : 'Recurring clients', desc: lang === 'es' ? 'con seguimiento proactivo' : 'with proactive follow-up' }
  ];
  
  return (
    <section className="relative py-20 md:py-32 bg-zinc-950 overflow-hidden" data-theme="dark" data-testid="results-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
        >
          {results.map((result, idx) => (
            <motion.div
              key={`result-${idx}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              className="text-center p-4 md:p-6"
            >
              <span className="block text-4xl md:text-6xl lg:text-7xl font-grotesk font-bold text-white mb-2">
                {result.value}
              </span>
              <span className="block text-sm md:text-base font-medium text-white mb-1">
                {result.label}
              </span>
              <span className="block text-xs md:text-sm text-zinc-500">
                {result.desc}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== NEXUM PLATFORM DIAGRAM ====================
const NexumPlatformDiagram = () => {
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  
  const tabs = [
    { id: 'voice', label: lang === 'es' ? 'Voz adaptativa' : 'Adaptive voice' },
    { id: 'attention', label: lang === 'es' ? 'Atención inteligente' : 'Smart attention' },
    { id: 'memory', label: lang === 'es' ? 'Memoria operativa' : 'Operative memory' }
  ];
  
  const voiceData = {
    title: 'Nexum Voice Engine',
    description: lang === 'es' 
      ? 'Nexum habla con voz natural, fluida y ajustada a cada cliente, sin sensación de bot rígido.'
      : 'Nexum speaks with natural, fluid voice adjusted to each client, without rigid bot feeling.',
    categories: [
      {
        name: lang === 'es' ? 'TECNOLOGÍA VOCAL' : 'VOCAL TECHNOLOGY',
        items: [
          { icon: Zap, label: lang === 'es' ? 'Baja latencia' : 'Low latency' },
          { icon: Mic, label: lang === 'es' ? 'Naturalidad' : 'Naturalness' },
          { icon: Pause, label: lang === 'es' ? 'Interrupciones' : 'Interruptions' },
          { icon: Waves, label: lang === 'es' ? 'Fluidez' : 'Fluency' }
        ]
      },
      {
        name: lang === 'es' ? 'CAPA HUMANA' : 'HUMAN LAYER',
        items: [
          { icon: Heart, label: lang === 'es' ? 'Empatía' : 'Empathy' },
          { icon: Settings, label: lang === 'es' ? 'Tono' : 'Tone' },
          { icon: Target, label: lang === 'es' ? 'Claridad' : 'Clarity' },
          { icon: Headphones, label: lang === 'es' ? 'Escucha' : 'Listening' }
        ]
      },
      {
        name: lang === 'es' ? 'ADAPTACIÓN' : 'ADAPTATION',
        items: [
          { icon: User, label: lang === 'es' ? 'Tipo de cliente' : 'Client type' },
          { icon: Brain, label: lang === 'es' ? 'Estado emocional' : 'Emotional state' },
          { icon: Globe, label: lang === 'es' ? 'Idioma' : 'Language' },
          { icon: MessageCircle, label: lang === 'es' ? 'Contexto' : 'Context' }
        ]
      },
      {
        name: lang === 'es' ? 'EXPERIENCIA' : 'EXPERIENCE',
        items: [
          { icon: Shield, label: lang === 'es' ? 'Confianza' : 'Trust' },
          { icon: MessageSquare, label: lang === 'es' ? 'Conversación real' : 'Real conversation' },
          { icon: ThumbsUp, label: lang === 'es' ? 'Menos rechazo' : 'Less rejection' },
          { icon: TrendingUp, label: lang === 'es' ? 'Más conversión' : 'More conversion' }
        ]
      }
    ],
    result: lang === 'es' ? 'Una voz que no suena a bot, sino a atención real.' : 'A voice that doesn\'t sound like a bot, but like real attention.'
  };
  
  const attentionData = {
    title: 'NexumAI Core',
    description: lang === 'es'
      ? 'Nexum recibe cada interacción, entiende la intención y ejecuta la siguiente acción automáticamente.'
      : 'Nexum receives each interaction, understands intent and executes the next action automatically.',
    categories: [
      {
        name: lang === 'es' ? 'CANALES' : 'CHANNELS',
        items: [
          { icon: Phone, label: lang === 'es' ? 'Llamadas' : 'Calls' },
          { icon: MessageCircle, label: 'WhatsApp' },
          { icon: Monitor, label: 'Web' },
          { icon: FileText, label: lang === 'es' ? 'Formularios' : 'Forms' }
        ]
      },
      {
        name: lang === 'es' ? 'COMPRENSIÓN' : 'UNDERSTANDING',
        items: [
          { icon: Target, label: lang === 'es' ? 'Intención' : 'Intent' },
          { icon: AlertTriangle, label: lang === 'es' ? 'Urgencia' : 'Urgency' },
          { icon: MessageCircle, label: lang === 'es' ? 'Contexto' : 'Context' },
          { icon: Star, label: lang === 'es' ? 'Prioridad' : 'Priority' }
        ]
      },
      {
        name: lang === 'es' ? 'ORQUESTACIÓN' : 'ORCHESTRATION',
        items: [
          { icon: CalendarCheck, label: lang === 'es' ? 'Agenda' : 'Calendar' },
          { icon: Settings, label: lang === 'es' ? 'Reglas' : 'Rules' },
          { icon: Clock, label: lang === 'es' ? 'Disponibilidad' : 'Availability' },
          { icon: ArrowUp, label: lang === 'es' ? 'Escalado' : 'Escalation' }
        ]
      },
      {
        name: lang === 'es' ? 'ACCIÓN' : 'ACTION',
        items: [
          { icon: Send, label: lang === 'es' ? 'Responde' : 'Responds' },
          { icon: CalendarCheck, label: lang === 'es' ? 'Agenda' : 'Books' },
          { icon: Bell, label: lang === 'es' ? 'Recuerda' : 'Reminds' },
          { icon: RefreshCw, label: lang === 'es' ? 'Seguimiento' : 'Follow-up' }
        ]
      }
    ],
    result: lang === 'es' ? 'Cada interacción se convierte en una acción útil.' : 'Every interaction becomes a useful action.'
  };
  
  const memoryData = {
    title: 'NexumAI Core',
    description: lang === 'es'
      ? 'Nexum adapta cada respuesta al cliente, al historial y a la necesidad real del momento.'
      : 'Nexum adapts each response to the client, history and real need of the moment.',
    categories: [
      {
        name: lang === 'es' ? 'DATOS' : 'DATA',
        items: [
          { icon: User, label: lang === 'es' ? 'Cliente' : 'Client' },
          { icon: Car, label: lang === 'es' ? 'Vehículo' : 'Vehicle' },
          { icon: Clock, label: lang === 'es' ? 'Historial' : 'History' },
          { icon: Settings, label: lang === 'es' ? 'Preferencias' : 'Preferences' }
        ]
      },
      {
        name: lang === 'es' ? 'CONTEXTO' : 'CONTEXT',
        items: [
          { icon: Wrench, label: lang === 'es' ? 'Avería' : 'Breakdown' },
          { icon: Wrench, label: lang === 'es' ? 'Mantenimiento' : 'Maintenance' },
          { icon: AlertTriangle, label: lang === 'es' ? 'Urgencia' : 'Urgency' },
          { icon: Clock, label: lang === 'es' ? 'Disponibilidad' : 'Availability' }
        ]
      },
      {
        name: lang === 'es' ? 'PREDICCIÓN' : 'PREDICTION',
        items: [
          { icon: ArrowRight, label: lang === 'es' ? 'Próxima necesidad' : 'Next need' },
          { icon: AlertCircle, label: lang === 'es' ? 'Riesgo' : 'Risk' },
          { icon: Lightbulb, label: lang === 'es' ? 'Recomendación' : 'Recommendation' },
          { icon: TrendingUp, label: lang === 'es' ? 'Oportunidad' : 'Opportunity' }
        ]
      },
      {
        name: lang === 'es' ? 'RESPUESTA' : 'RESPONSE',
        items: [
          { icon: Check, label: lang === 'es' ? 'Respuesta precisa' : 'Precise response' },
          { icon: Tag, label: lang === 'es' ? 'Oferta correcta' : 'Right offer' },
          { icon: RefreshCw, label: lang === 'es' ? 'Seguimiento' : 'Follow-up' },
          { icon: Link, label: lang === 'es' ? 'Continuidad' : 'Continuity' }
        ]
      }
    ],
    result: lang === 'es' ? 'Cada cliente se siente único porque lo es.' : 'Every client feels unique because they are.'
  };
  
  const tabsData = [voiceData, attentionData, memoryData];
  const currentData = tabsData[activeTab];
  
  return (
    <section className="pt-4 pb-16 md:pt-8 md:pb-24 bg-white overflow-hidden" data-testid="platform-diagram">
      <div className="max-w-6xl mx-auto px-5 md:px-12">
        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8 lg:mb-12">
          {tabs.map((tab, idx) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(idx)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-300 ${
                activeTab === idx 
                  ? 'bg-zinc-900 text-white shadow-md' 
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Desktop: Diagram left, Text right */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Diagram Container - Left on desktop */}
          <div
            className="relative rounded-2xl p-5 md:p-8 shadow-xl shadow-zinc-400/20 w-full lg:w-[55%]"
            style={{ background: 'linear-gradient(180deg, #e8e4dc 0%, #d4cfc4 100%)' }}
          >
            {/* Description */}
            <p className="text-center text-zinc-600 text-xs md:text-sm mb-6 max-w-sm mx-auto">
              {currentData.description}
            </p>
            
            {/* Central Button */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded-full shadow-lg shadow-zinc-900/20">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="font-semibold text-xs">{currentData.title}</span>
              </div>
            </div>
            
            {/* Categories Flow */}
            <div className="space-y-4">
              {currentData.categories.map((category, catIdx) => (
                <div key={catIdx} className="relative">
                  {/* Category Label */}
                  <div className="mb-2">
                    <span className="text-[9px] font-semibold tracking-[0.15em] text-zinc-500">
                      {category.name}
                    </span>
                  </div>
                  
                  {/* Pills */}
                  <div className="flex flex-wrap gap-1.5 relative z-10">
                    {category.items.map((item, itemIdx) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={itemIdx}
                          className="inline-flex items-center gap-1.5 bg-white px-3 py-1 rounded-full shadow-sm shadow-zinc-300/50 border border-zinc-100"
                        >
                          <Icon className="w-3 h-3 text-zinc-500" strokeWidth={1.5} />
                          <span className="text-xs text-zinc-700">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Result Card */}
            <div className="mt-6 bg-white/90 backdrop-blur rounded-xl p-3 flex items-center gap-3 shadow-md shadow-zinc-400/15">
              <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
              <div>
                <span className="text-[9px] font-semibold tracking-[0.15em] text-zinc-400 block">RESULTADO</span>
                <p className="text-zinc-800 font-medium text-sm">{currentData.result}</p>
              </div>
            </div>
          </div>
          
          {/* Text Content - Right on desktop */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:w-[45%] text-center lg:text-left relative"
          >
            {/* Subtle connecting line - only visible on desktop */}
            <div className="hidden lg:block absolute left-0 top-1/2 -translate-x-full w-12 h-px">
              <div className="w-full h-full bg-gradient-to-l from-zinc-300 to-transparent" />
              <motion.div 
                className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-zinc-300"
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            
            <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-400 mb-4 block">
              {lang === 'es' ? 'Automatización inteligente' : 'Smart automation'}
            </span>
            
            <h3 className="text-xl md:text-2xl lg:text-3xl font-grotesk font-semibold text-zinc-900 mb-4 leading-tight">
              {lang === 'es' 
                ? 'Todo conectado. Todo automático.' 
                : 'Everything connected. Everything automatic.'}
            </h3>
            
            <p className="text-sm md:text-base text-zinc-500 leading-relaxed mb-6">
              {lang === 'es' 
                ? 'Desde que un cliente llama hasta que se convierte en una cita agendada, Nexum procesa la información, actualiza tu CRM y envía confirmaciones. Sin intervención manual.' 
                : 'From the moment a customer calls until it becomes a scheduled appointment, Nexum processes the information, updates your CRM and sends confirmations. No manual intervention.'}
            </p>
            
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-zinc-600" />
                </div>
                <span className="text-sm text-zinc-600">{lang === 'es' ? 'Sincronización en tiempo real' : 'Real-time sync'}</span>
              </div>
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-zinc-600" />
                </div>
                <span className="text-sm text-zinc-600">{lang === 'es' ? 'Integración con tu sistema actual' : 'Integration with your current system'}</span>
              </div>
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-zinc-600" />
                </div>
                <span className="text-sm text-zinc-600">{lang === 'es' ? 'Menos errores manuales' : 'Fewer manual errors'}</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Predictive Automation Mini Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 md:mt-14 pt-8 md:pt-10 border-t border-zinc-200"
        >
          <div className="text-center mb-6 md:mb-8">
            <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-400 mb-3 block">
              {lang === 'es' ? 'Automatización predictiva' : 'Predictive automation'}
            </span>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-grotesk font-semibold text-zinc-900 leading-tight">
              {lang === 'es' 
                ? 'Mantenimiento antes de que falle.' 
                : 'Maintenance before it fails.'}
            </h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 md:gap-5">
            {/* Card 1 - Kilometraje */}
            <div className="text-center p-4 md:p-5 bg-zinc-50 border border-zinc-100">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-zinc-900 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-grotesk font-semibold text-zinc-900 mb-2">
                {lang === 'es' ? 'Por kilómetros' : 'By mileage'}
              </h4>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {lang === 'es' 
                  ? 'Distribución a 120.000km, frenos a 40.000km. Avisa al cliente antes.' 
                  : 'Timing belt at 120,000km, brakes at 40,000km. Alerts client beforehand.'}
              </p>
            </div>
            
            {/* Card 2 - Edad del vehículo */}
            <div className="text-center p-4 md:p-5 bg-zinc-50 border border-zinc-100">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-zinc-900 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-grotesk font-semibold text-zinc-900 mb-2">
                {lang === 'es' ? 'Por antigüedad' : 'By vehicle age'}
              </h4>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {lang === 'es' 
                  ? 'Batería a los 4 años, amortiguadores a los 80.000km.' 
                  : 'Battery at 4 years, shocks at 80,000km.'}
              </p>
            </div>
            
            {/* Card 3 - Recambios automáticos */}
            <div className="text-center p-4 md:p-5 bg-zinc-50 border border-zinc-100">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-zinc-900 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-grotesk font-semibold text-zinc-900 mb-2">
                {lang === 'es' ? 'Recambios automáticos' : 'Auto parts ordering'}
              </h4>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {lang === 'es' 
                  ? 'Consulta info oficial de marca y solicita recambios según modelo y km.' 
                  : 'Queries official brand data and orders parts by model and mileage.'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== INTEGRATION SECTION ====================
const IntegrationSection = () => {
  const { lang } = useLanguage();
  
  // SVG Logos inline
  const LogoGoogle = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
  
  const LogoMicrosoft = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
    </svg>
  );
  
  const LogoSalesforce = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M10.006 5.415a4.195 4.195 0 0 1 3.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.16 5.22c-.45 0-.884-.06-1.305-.165a3.818 3.818 0 0 1-3.33 1.965 3.57 3.57 0 0 1-1.65-.39 4.678 4.678 0 0 1-4.049 2.34c-2.13 0-3.96-1.425-4.544-3.39-.27.045-.539.074-.81.074C1.516 16.739 0 15.174 0 13.264s1.516-3.48 3.15-3.48c.255 0 .51.03.75.074a4.156 4.156 0 0 1 3.93-2.79c.81 0 1.575.24 2.176.645v-.298z"/>
    </svg>
  );
  
  const LogoWhatsApp = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
  
  const LogoSlack = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  );
  
  const LogoHubSpot = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.984v-.066A2.2 2.2 0 0017.238.84h-.067a2.2 2.2 0 00-2.193 2.193v.066a2.2 2.2 0 001.267 1.984v2.846a6.27 6.27 0 00-2.932 1.49l-7.74-6.02a2.927 2.927 0 00.096-.725 2.834 2.834 0 10-2.834 2.834c.39 0 .757-.085 1.096-.227l7.582 5.894a6.291 6.291 0 00-.903 3.263 6.387 6.387 0 001.017 3.443l-2.457 2.457a2.1 2.1 0 00-.65-.114 2.153 2.153 0 102.152 2.152c0-.233-.045-.454-.114-.663l2.415-2.415a6.327 6.327 0 109.065-9.594 6.274 6.274 0 00-3.874-1.325zm-.96 9.946a3.323 3.323 0 01-3.319-3.319 3.323 3.323 0 013.32-3.32 3.323 3.323 0 013.318 3.32 3.323 3.323 0 01-3.319 3.319z"/>
    </svg>
  );
  
  const LogoZapier = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M15.478 7.156l-2.706 2.706h5.39l.424-.423v-.02l.013-.01 2.62-2.62a8.29 8.29 0 00-1.63-2.163l-2.63 2.627-.012.013-.02-.003-.424.424v-.002l-.007.006-.007-.006v.002l-.424-.424-.02.003-.013-.013-2.626-2.627a8.222 8.222 0 00-2.168 1.63l2.62 2.62.01.012-.002.02.423.424h.002l.006.007-.006.007h-.002l-.423.424.002.02-.01.013-2.62 2.62a8.29 8.29 0 001.63 2.163l2.63-2.627.012-.013.02.003.424-.424v.002l.007-.007.007.007v-.002l.424.424.02-.003.013.013 2.626 2.627a8.222 8.222 0 002.168-1.63l-2.62-2.62-.01-.012.002-.02-.423-.424h-.002l-.006-.007.006-.007h.002l.423-.424-.002-.02.01-.013 2.62-2.62a8.29 8.29 0 00-1.63-2.163l-2.63 2.627-.012.013-.02-.003zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.797c-5.41 0-9.797-4.387-9.797-9.797S6.59 2.203 12 2.203 21.797 6.59 21.797 12 17.41 21.797 12 21.797zm2.772-12.542H13.69v-2.76h-3.38v2.76H9.228v3.402h1.082v2.76h3.38v-2.76h1.082v-3.402z"/>
    </svg>
  );
  
  const LogoSAP = () => (
    <svg viewBox="0 0 24 24" className="h-8 md:h-10 w-auto fill-current">
      <path d="M0 6.064v11.872h24V6.064H0zm12.752 9.5l-.827-1.94H8.987l-.827 1.94H6.602L9.55 8.437h1.848l2.918 7.127h-1.564zm4.79 0h-1.462V8.437h1.463v7.127zm4.66-5.766c-.134-.523-.503-.872-1.098-.872-.758 0-1.323.534-1.323 1.436 0 .892.565 1.425 1.323 1.425.595 0 .964-.349 1.098-.872h1.473c-.226 1.313-1.282 2.182-2.57 2.182-1.605 0-2.813-1.138-2.813-2.735 0-1.607 1.208-2.746 2.812-2.746 1.289 0 2.345.87 2.571 2.182h-1.473zm-11.555 4.84l-1.134-2.87-1.144 2.87h2.278z"/>
    </svg>
  );
  
  const integrationLogos = [
    { name: 'Google', Component: LogoGoogle },
    { name: 'Microsoft', Component: LogoMicrosoft },
    { name: 'Salesforce', Component: LogoSalesforce },
    { name: 'WhatsApp', Component: LogoWhatsApp },
    { name: 'Slack', Component: LogoSlack },
    { name: 'HubSpot', Component: LogoHubSpot },
    { name: 'Zapier', Component: LogoZapier },
    { name: 'SAP', Component: LogoSAP },
  ];
  
  const steps = [
    {
      num: '01',
      title: lang === 'es' ? 'Análisis' : 'Analysis',
      desc: lang === 'es' 
        ? 'Estudiamos tu operativa, sistemas actuales y flujos de trabajo.' 
        : 'We study your operations, current systems and workflows.'
    },
    {
      num: '02',
      title: lang === 'es' ? 'Configuración' : 'Configuration',
      desc: lang === 'es' 
        ? 'Adaptamos Nexum a tu negocio: horarios, servicios, reglas, tono.' 
        : 'We adapt Nexum to your business: schedules, services, rules, tone.'
    },
    {
      num: '03',
      title: lang === 'es' ? 'Integración' : 'Integration',
      desc: lang === 'es' 
        ? 'Conectamos con tu DMS, calendario y canales de comunicación.' 
        : 'We connect with your DMS, calendar and communication channels.'
    },
    {
      num: '04',
      title: lang === 'es' ? 'Activación' : 'Activation',
      desc: lang === 'es' 
        ? 'Formamos a tu equipo y activamos Nexum en producción.' 
        : 'We train your team and activate Nexum in production.'
    }
  ];
  
  return (
    <section className="py-20 md:py-32 bg-zinc-950 overflow-hidden" data-theme="dark" data-testid="integration-section">
      <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-500 mb-4 block">
            {lang === 'es' ? 'Integración a medida' : 'Custom integration'}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold text-white tracking-tight leading-[1.1] mb-4">
            {lang === 'es' 
              ? 'Nos adaptamos a tu negocio.' 
              : 'We adapt to your business.'}
          </h2>
          <p className="text-base md:text-lg text-zinc-500 max-w-2xl mx-auto">
            {lang === 'es' 
              ? 'Cada implementación es única. Conectamos con tus sistemas existentes sin cambiar tu forma de trabajar.' 
              : 'Every implementation is unique. We connect with your existing systems without changing how you work.'}
          </p>
        </motion.div>
        
        {/* Integrations Marquee with Real Logos */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20 md:mb-28"
        >
          <div className="relative overflow-hidden py-8">
            {/* Gradient masks */}
            <div className="absolute left-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-r from-zinc-950 to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-l from-zinc-950 to-transparent z-10" />
            
            {/* Scrolling logos */}
            <div className="flex animate-marquee-slow whitespace-nowrap">
              {[...Array(3)].map((_, i) => (
                <div key={`int-${i}`} className="flex items-center gap-16 md:gap-24 mx-12 md:mx-16">
                  {integrationLogos.map((item, j) => (
                    <div 
                      key={`logo-${i}-${j}`} 
                      className="text-zinc-500 hover:text-white transition-colors duration-300"
                    >
                      <item.Component />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {/* Custom integrations text */}
          <p className="text-center text-xs md:text-sm text-zinc-600 mt-4">
            {lang === 'es' ? '+ Integraciones personalizadas bajo demanda' : '+ Custom integrations on demand'}
          </p>
        </motion.div>
        
        {/* Process Steps */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
        >
          <div className="grid md:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={`step-${idx}`}
                variants={fadeIn}
                className="group relative"
              >
                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px">
                    <div className="w-full h-full bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800" />
                  </div>
                )}
                
                <div className="relative bg-zinc-900/30 border border-zinc-800/50 p-6 md:p-8 hover:border-zinc-700 hover:bg-zinc-900/50 transition-all duration-500">
                  {/* Step number */}
                  <span className="text-4xl md:text-5xl font-grotesk font-bold text-zinc-800 group-hover:text-zinc-700 transition-colors">
                    {step.num}
                  </span>
                  
                  {/* Content */}
                  <h4 className="text-lg md:text-xl font-grotesk font-semibold text-white mt-4 mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16 md:mt-20"
        >
          <p className="text-zinc-500 text-sm md:text-base mb-6">
            {lang === 'es' 
              ? '¿Usas un sistema que no está en la lista?' 
              : 'Using a system not on the list?'}
          </p>
          <a 
            href={CALENDLY_URL} onClick={openCalendly} 
            className="inline-flex items-center gap-2 text-white font-medium text-sm border-b border-zinc-700 pb-1 hover:border-white transition-colors group"
          >
            {lang === 'es' ? 'Hablemos de tu caso' : "Let's discuss your case"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== STATEMENT TEXT BLOCKS ====================
const StatementBlock1 = () => {
  const { lang } = useLanguage();
  
  return (
    <section className="pt-20 pb-8 md:pt-32 md:pb-12 bg-white overflow-hidden">
      <div className="max-w-5xl mx-auto px-5 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-zinc-400 mb-6 block">
            {lang === 'es' ? 'Atención al cliente' : 'Customer service'}
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-grotesk font-semibold tracking-tight leading-[1.2] text-zinc-900">
            {lang === 'es' 
              ? 'Las llamadas son solo el comienzo. El seguimiento lo cambia todo.' 
              : 'Calls are just the beginning. Follow-up changes everything.'}
          </h2>
        </motion.div>
      </div>
    </section>
  );
};

const StatementBlock2 = () => {
  const { lang } = useLanguage();
  
  return (
    <section className="py-20 md:py-32 bg-zinc-950 overflow-hidden" data-theme="dark">
      <div className="max-w-5xl mx-auto px-5 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-zinc-600 mb-8 block">
            {lang === 'es' ? 'Automatización inteligente' : 'Smart automation'}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-medium tracking-tight leading-[1.1]">
            <span className="text-white">{lang === 'es' ? 'Cada cliente tiene un ' : 'Every customer has a '}</span>
            <span className="italic text-zinc-900">{lang === 'es' ? 'historial.' : 'history.'}</span>
            <br />
            <span className="text-white">{lang === 'es' ? 'Nexum lo ' : 'Nexum '}</span>
            <span className="italic text-zinc-900">{lang === 'es' ? 'conoce' : 'knows it'}</span>
            <span className="text-white">{lang === 'es' ? ' y lo usa.' : ' and uses it.'}</span>
          </h2>
        </motion.div>
      </div>
    </section>
  );
};

const StatementBlock3 = () => {
  const { lang } = useLanguage();
  
  return (
    <section className="py-20 md:py-32 bg-[#fafafa] overflow-hidden">
      <div className="max-w-5xl mx-auto px-5 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-zinc-400 mb-8 block">
            {lang === 'es' ? 'Mantenimiento predictivo' : 'Predictive maintenance'}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-medium tracking-tight leading-[1.1]">
            <span className="text-zinc-900">{lang === 'es' ? 'Distribución a ' : 'Timing belt at '}</span>
            <span className="italic text-zinc-900">120.000km.</span>
            <br />
            <span className="text-zinc-900">{lang === 'es' ? 'Frenos a ' : 'Brakes at '}</span>
            <span className="italic text-zinc-900">40.000km.</span>
            <br />
            <span className="text-zinc-900">{lang === 'es' ? 'Nexum avisa ' : 'Nexum alerts '}</span>
            <span className="italic text-zinc-900">{lang === 'es' ? 'antes.' : 'early.'}</span>
          </h2>
        </motion.div>
      </div>
    </section>
  );
};

const StatementBlock4 = () => {
  const { lang } = useLanguage();
  
  return (
    <section className="py-20 md:py-32 bg-black overflow-hidden" data-theme="dark">
      <div className="max-w-5xl mx-auto px-5 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-zinc-600 mb-8 block">
            {lang === 'es' ? 'Recuperación de clientes' : 'Client recovery'}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-medium tracking-tight leading-[1.1]">
            <span className="text-white">{lang === 'es' ? '¿Clientes inactivos?' : 'Inactive clients?'}</span>
            <br />
            <span className="text-white">{lang === 'es' ? 'Nexum ' : 'Nexum '}</span>
            <span className="italic text-zinc-900">{lang === 'es' ? 'reactiva' : 'reactivates'}</span>
            <span className="text-white">{lang === 'es' ? ' la relación.' : ' the relationship.'}</span>
          </h2>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== PREMIUM TESTIMONIALS ====================
const PremiumTestimonialsSection = () => {
  const { lang } = useLanguage();
  
  const testimonials = [
    {
      quote: lang === 'es' 
        ? 'Antes perdíamos 4 de cada 10 llamadas. Ahora Nexum las atiende todas y agenda citas mientras trabajamos en el taller.'
        : 'We used to miss 4 out of 10 calls. Now Nexum answers them all and books appointments while we work in the shop.',
      author: 'Carlos Martínez',
      role: lang === 'es' ? 'Propietario, Taller Premium Motors' : 'Owner, Premium Motors Workshop',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face'
    },
    {
      quote: lang === 'es'
        ? 'El seguimiento de mantenimientos es brutal. Los clientes nos dicen que les encanta recibir el aviso antes de que algo falle.'
        : 'The maintenance follow-up is incredible. Clients tell us they love getting alerts before something fails.',
      author: 'Ana Rodríguez',
      role: lang === 'es' ? 'Directora, Concesionario AutoElite' : 'Director, AutoElite Dealership',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face'
    },
    {
      quote: lang === 'es'
        ? 'La inversión se recupera en el primer mes. Un solo cliente recuperado por el seguimiento automático ya cubre el coste.'
        : 'The investment pays off in the first month. One client recovered by automatic follow-up already covers the cost.',
      author: 'Miguel Sánchez', 
      role: lang === 'es' ? 'Gerente, Servicio Oficial BMW' : 'Manager, Official BMW Service',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face'
    }
  ];
  
  return (
    <section className="py-24 md:py-36 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-400 mb-6 block">
            {lang === 'es' ? 'Testimonios' : 'Testimonials'}
          </span>
          <h2 className="text-3xl md:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900">
            {lang === 'es' ? 'Lo que dicen nuestros clientes' : 'What our clients say'}
          </h2>
        </motion.div>
        
        {/* Testimonials Grid */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {testimonials.map((testimonial, idx) => (
            <motion.div
              key={`testimonial-${idx}`}
              variants={fadeIn}
              whileHover={{ y: -6 }}
              className="group relative bg-zinc-50 p-6 md:p-8 border border-zinc-100 hover:border-zinc-200 transition-all duration-300"
            >
              {/* Quote Mark */}
              <div className="absolute top-6 right-6 text-6xl font-serif text-zinc-200 leading-none">"</div>
              
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-zinc-900 text-zinc-900" />
                ))}
              </div>
              
              {/* Quote */}
              <p className="text-zinc-700 leading-relaxed mb-8 relative z-10">
                "{testimonial.quote}"
              </p>
              
              {/* Author */}
              <div className="flex items-center gap-4 pt-6 border-t border-zinc-200">
                <img 
                  src={testimonial.image}
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="font-semibold text-zinc-900">{testimonial.author}</p>
                  <p className="text-sm text-zinc-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== PROBLEM SECTION ====================
const ProblemSection = () => {
  const { t } = useLanguage();
  const icons = [PhoneOff, Clock, Calendar, UserX, MessageSquare, Languages, Moon];
  
  return (
    <section className="section-padding bg-light" data-testid="problem-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px", amount: 0.2 }} variants={stagger}>
          <motion.span variants={fadeInRight} className="overline mb-5 block">{t.problem.overline}</motion.span>
          <motion.h2 variants={fadeInRight} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900 mb-6 max-w-3xl">{t.problem.title}</motion.h2>
          <motion.p variants={fadeInRight} className="text-lg text-zinc-600 leading-relaxed mb-14 max-w-2xl">{t.problem.subtitle}</motion.p>
          
          <motion.div variants={staggerFast} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {t.problem.items.map((item, idx) => {
              const Icon = icons[idx];
              return (
                <motion.div 
                  key={`problem-${idx}`} 
                  variants={scaleIn} 
                  whileHover={{ y: -2 }}
                  className="flex items-center gap-2 sm:gap-3 md:gap-4 p-2 sm:p-3 md:p-5 bg-white border border-zinc-200 hover:border-zinc-300 hover:shadow-sm transition-all duration-300"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-zinc-400" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] sm:text-xs md:text-sm text-zinc-700 leading-tight">{item}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== CAPABILITIES SECTION ====================
const CapabilitiesSection = () => {
  const { t } = useLanguage();
  const icons = [MessageCircle, Globe, Clock, CalendarCheck, Bell, Send];
  
  return (
    <section id="capabilities" className="py-28 md:py-36 bg-[#fafafa]" data-testid="capabilities-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerFromCenter} className="text-center mb-20">
          <motion.span variants={blurIn} className="text-[11px] font-medium tracking-[0.2em] uppercase text-zinc-400 mb-6 block">{t.capabilities.overline}</motion.span>
          <motion.h2 variants={scaleFromCenter} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900">{t.capabilities.title}</motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={staggerFromCenter} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-5">
          {t.capabilities.items.slice(0, 6).map((item, idx) => {
            const Icon = icons[idx];
            return (
              <motion.div 
                key={`cap-${idx}`} 
                variants={expandFromCenter} 
                whileHover={{ y: -4 }}
                className="bg-white border border-zinc-200 p-3 sm:p-4 md:p-6 lg:p-8 hover:border-zinc-300 transition-all duration-300"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-11 md:h-11 bg-zinc-100 flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                  <Icon className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 text-zinc-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-grotesk font-semibold text-zinc-900 mb-1 sm:mb-2">{item.title}</h3>
                <p className="text-[10px] sm:text-xs md:text-sm text-zinc-500 leading-relaxed hidden sm:block">{item.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== HERO ANIMATION MOBILE (OPTIMIZED) ====================
const HeroAnimationMobile = () => {
  const { lang } = useLanguage();
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Animaciones simplificadas - más rápidas para móvil
  const w1X = useTransform(scrollYProgress, [0, 0.12], [300, 0]);
  const w1Scale = useTransform(scrollYProgress, [0, 0.12], [3.5, 0.3]);
  const w1Opacity = useTransform(scrollYProgress, [0, 0.04, 0.08, 0.14], [0, 1, 1, 0]);

  const w2X = useTransform(scrollYProgress, [0.02, 0.14], [-300, 0]);
  const w2Scale = useTransform(scrollYProgress, [0.02, 0.14], [3.5, 0.3]);
  const w2Opacity = useTransform(scrollYProgress, [0.02, 0.06, 0.10, 0.16], [0, 1, 1, 0]);

  const w3Y = useTransform(scrollYProgress, [0.04, 0.16], [-250, 0]);
  const w3Scale = useTransform(scrollYProgress, [0.04, 0.16], [3.5, 0.3]);
  const w3Opacity = useTransform(scrollYProgress, [0.04, 0.08, 0.12, 0.18], [0, 1, 1, 0]);

  const w4X = useTransform(scrollYProgress, [0.06, 0.18], [280, 0]);
  const w4Scale = useTransform(scrollYProgress, [0.06, 0.18], [3.5, 0.3]);
  const w4Opacity = useTransform(scrollYProgress, [0.06, 0.10, 0.14, 0.20], [0, 1, 1, 0]);

  const w5Y = useTransform(scrollYProgress, [0.08, 0.20], [250, 0]);
  const w5Scale = useTransform(scrollYProgress, [0.08, 0.20], [3.5, 0.3]);
  const w5Opacity = useTransform(scrollYProgress, [0.08, 0.12, 0.16, 0.22], [0, 1, 1, 0]);

  // Nexum Voice reveal - aparece cuando las palabras desaparecen
  const nexumOpacity = useTransform(scrollYProgress, [0.20, 0.30], [0, 1]);
  const nexumScale = useTransform(scrollYProgress, [0.20, 0.35], [0.85, 1]);
  const subtitleOpacity = useTransform(scrollYProgress, [0.40, 0.52], [0, 1]);

  const wordStyle = {
    fontFamily: "'Space Grotesk', sans-serif",
    fontWeight: 600,
    letterSpacing: '-0.05em',
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    willChange: 'transform, opacity'
  };

  return (
    <div ref={containerRef} className="h-[300vh] bg-black relative" data-testid="hero-animation-mobile" data-theme="dark">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        
        {/* Palabras animadas - versión móvil optimizada */}
        <motion.div style={{x: w1X, scale: w1Scale, opacity: w1Opacity, ...wordStyle}} className="text-4xl absolute whitespace-nowrap select-none z-0">Hello</motion.div>
        <motion.div style={{x: w2X, scale: w2Scale, opacity: w2Opacity, ...wordStyle}} className="text-4xl absolute whitespace-nowrap select-none z-0">Hola</motion.div>
        <motion.div style={{y: w3Y, scale: w3Scale, opacity: w3Opacity, ...wordStyle}} className="text-4xl absolute whitespace-nowrap select-none z-0">Bonjour</motion.div>
        <motion.div style={{x: w4X, scale: w4Scale, opacity: w4Opacity, ...wordStyle}} className="text-4xl absolute whitespace-nowrap select-none z-0">你好</motion.div>
        <motion.div style={{y: w5Y, scale: w5Scale, opacity: w5Opacity, ...wordStyle}} className="text-4xl absolute whitespace-nowrap select-none z-0">Ciao</motion.div>

        {/* Nexum Voice - aparece al final encima de las palabras */}
        <motion.div 
          style={{ opacity: nexumOpacity, scale: nexumScale }}
          className="absolute flex flex-col items-center gap-3 px-4 z-10"
        >
          <span className="text-[10px] font-medium tracking-[0.3em] uppercase text-zinc-600">
            {lang === 'es' ? 'Presentamos' : 'Introducing'}
          </span>
          <h2 
            className="text-4xl font-grotesk font-semibold bg-gradient-to-r from-zinc-300 via-white to-zinc-400 bg-clip-text text-transparent"
            style={{letterSpacing: '-0.03em'}}
          >
            Nexum Voice
          </h2>
          <motion.p 
            style={{ opacity: subtitleOpacity }}
            className="text-xs text-zinc-500 text-center max-w-[280px] leading-relaxed"
          >
            {lang === 'es' ? 'Agenda citas, atiende llamadas en más de 20 idiomas y recupera clientes inactivos, incluso fuera del horario comercial. Nuestro agente les avisa de cambio de aceite, campañas, e incluso conoce el historial de cada vehículo.' : 'Schedule appointments, answer calls in over 20 languages and recover inactive clients, even outside business hours. Our agent notifies them about oil changes, campaigns, and knows each vehicle\'s history.'}
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

// ==================== HERO ANIMATION DESKTOP (SCROLL) ====================
const HeroAnimationDesktop = () => {
  const { lang } = useLanguage();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const bgImage = "https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/o5ndff8m_EA7CADF3-1201-431A-8440-5F5DEAD73B6D%202-2.png";

  // Palabra 1: Hello
  const w1X = useTransform(scrollYProgress, [0, 0.35], [1500, 0]);
  const w1Y = useTransform(scrollYProgress, [0, 0.35], [0, 0]);
  const w1Scale = useTransform(scrollYProgress, [0, 0.35], [8, 0.25]);
  const w1Rotate = useTransform(scrollYProgress, [0, 0.35], [0, 180]);
  const w1Opacity = useTransform(scrollYProgress, [0, 0.15, 0.25, 0.35], [0, 1, 1, 0]);

  // Palabra 2: Hola
  const w2X = useTransform(scrollYProgress, [0.05, 0.40], [-1500, 0]);
  const w2Y = useTransform(scrollYProgress, [0.05, 0.40], [0, 0]);
  const w2Scale = useTransform(scrollYProgress, [0.05, 0.40], [8, 0.25]);
  const w2Rotate = useTransform(scrollYProgress, [0.05, 0.40], [0, -180]);
  const w2Opacity = useTransform(scrollYProgress, [0.05, 0.20, 0.30, 0.40], [0, 1, 1, 0]);

  // Palabra 3: Bonjour
  const w3X = useTransform(scrollYProgress, [0.10, 0.45], [0, 0]);
  const w3Y = useTransform(scrollYProgress, [0.10, 0.45], [-900, 0]);
  const w3Scale = useTransform(scrollYProgress, [0.10, 0.45], [8, 0.25]);
  const w3Rotate = useTransform(scrollYProgress, [0.10, 0.45], [0, 90]);
  const w3Opacity = useTransform(scrollYProgress, [0.10, 0.25, 0.35, 0.45], [0, 1, 1, 0]);

  // Palabra 4: こんにちは
  const w4X = useTransform(scrollYProgress, [0.15, 0.50], [1300, 0]);
  const w4Y = useTransform(scrollYProgress, [0.15, 0.50], [700, 0]);
  const w4Scale = useTransform(scrollYProgress, [0.15, 0.50], [8, 0.25]);
  const w4Rotate = useTransform(scrollYProgress, [0.15, 0.50], [0, 135]);
  const w4Opacity = useTransform(scrollYProgress, [0.15, 0.30, 0.40, 0.50], [0, 1, 1, 0]);

  // Palabra 5: Ciao
  const w5X = useTransform(scrollYProgress, [0.20, 0.55], [-1300, 0]);
  const w5Y = useTransform(scrollYProgress, [0.20, 0.55], [-700, 0]);
  const w5Scale = useTransform(scrollYProgress, [0.20, 0.55], [8, 0.25]);
  const w5Rotate = useTransform(scrollYProgress, [0.20, 0.55], [0, -135]);
  const w5Opacity = useTransform(scrollYProgress, [0.20, 0.35, 0.45, 0.55], [0, 1, 1, 0]);

  // Palabra 6: مرحبا
  const w6X = useTransform(scrollYProgress, [0.25, 0.60], [0, 0]);
  const w6Y = useTransform(scrollYProgress, [0.25, 0.60], [900, 0]);
  const w6Scale = useTransform(scrollYProgress, [0.25, 0.60], [8, 0.25]);
  const w6Rotate = useTransform(scrollYProgress, [0.25, 0.60], [0, -90]);
  const w6Opacity = useTransform(scrollYProgress, [0.25, 0.40, 0.50, 0.60], [0, 1, 1, 0]);

  // Palabra 7: Olá
  const w7X = useTransform(scrollYProgress, [0.30, 0.65], [1100, 0]);
  const w7Y = useTransform(scrollYProgress, [0.30, 0.65], [-500, 0]);
  const w7Scale = useTransform(scrollYProgress, [0.30, 0.65], [8, 0.25]);
  const w7Rotate = useTransform(scrollYProgress, [0.30, 0.65], [0, 160]);
  const w7Opacity = useTransform(scrollYProgress, [0.30, 0.45, 0.55, 0.65], [0, 1, 1, 0]);

  // Palabra 8: 你好
  const w8X = useTransform(scrollYProgress, [0.35, 0.70], [-1100, 0]);
  const w8Y = useTransform(scrollYProgress, [0.35, 0.70], [500, 0]);
  const w8Scale = useTransform(scrollYProgress, [0.35, 0.70], [8, 0.25]);
  const w8Rotate = useTransform(scrollYProgress, [0.35, 0.70], [0, -160]);
  const w8Opacity = useTransform(scrollYProgress, [0.35, 0.50, 0.60, 0.70], [0, 1, 1, 0]);

  // Nexum Voice letters
  const n1O = useTransform(scrollYProgress, [0.30, 0.40], [0, 1]);
  const n1S = useTransform(scrollYProgress, [0.30, 0.45], [0.5, 1]);
  const n2O = useTransform(scrollYProgress, [0.33, 0.43], [0, 1]);
  const n2S = useTransform(scrollYProgress, [0.33, 0.48], [0.5, 1]);
  const n3O = useTransform(scrollYProgress, [0.36, 0.46], [0, 1]);
  const n3S = useTransform(scrollYProgress, [0.36, 0.51], [0.5, 1]);
  const n4O = useTransform(scrollYProgress, [0.39, 0.49], [0, 1]);
  const n4S = useTransform(scrollYProgress, [0.39, 0.54], [0.5, 1]);
  const n5O = useTransform(scrollYProgress, [0.42, 0.52], [0, 1]);
  const n5S = useTransform(scrollYProgress, [0.42, 0.57], [0.5, 1]);
  const n7O = useTransform(scrollYProgress, [0.48, 0.58], [0, 1]);
  const n7S = useTransform(scrollYProgress, [0.48, 0.63], [0.5, 1]);
  const n8O = useTransform(scrollYProgress, [0.51, 0.61], [0, 1]);
  const n8S = useTransform(scrollYProgress, [0.51, 0.66], [0.5, 1]);
  const n9O = useTransform(scrollYProgress, [0.54, 0.64], [0, 1]);
  const n9S = useTransform(scrollYProgress, [0.54, 0.69], [0.5, 1]);
  const n10O = useTransform(scrollYProgress, [0.57, 0.67], [0, 1]);
  const n10S = useTransform(scrollYProgress, [0.57, 0.72], [0.5, 1]);
  const n11O = useTransform(scrollYProgress, [0.60, 0.70], [0, 1]);
  const n11S = useTransform(scrollYProgress, [0.60, 0.75], [0.5, 1]);

  // Presentamos
  const presentamosOpacity = useTransform(scrollYProgress, [0.65, 0.75], [0, 1]);

  // Tagline — appears later, requires more scroll
  const taglineOpacity = useTransform(scrollYProgress, [0.80, 0.92], [0, 1]);

  // Brillo de fondo
  const glowOpacity = useTransform(scrollYProgress, [0.75, 0.85], [0, 0.5]);

  const wordStyle = {
    fontFamily: "'Space Grotesk', sans-serif",
    fontWeight: 600,
    letterSpacing: '-0.08em',
    background: `url(${bgImage})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    willChange: 'transform, opacity'
  };

  return (
    <div ref={containerRef} className="h-[400vh] bg-black relative" data-testid="hero-animation" data-theme="dark">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        
        {/* Palabras animadas con todas las transformaciones */}
        <motion.div style={{x: w1X, y: w1Y, scale: w1Scale, rotate: w1Rotate, opacity: w1Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">Hello</motion.div>
        <motion.div style={{x: w2X, y: w2Y, scale: w2Scale, rotate: w2Rotate, opacity: w2Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">Hola</motion.div>
        <motion.div style={{x: w3X, y: w3Y, scale: w3Scale, rotate: w3Rotate, opacity: w3Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">Bonjour</motion.div>
        <motion.div style={{x: w4X, y: w4Y, scale: w4Scale, rotate: w4Rotate, opacity: w4Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">こんにちは</motion.div>
        <motion.div style={{x: w5X, y: w5Y, scale: w5Scale, rotate: w5Rotate, opacity: w5Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">Ciao</motion.div>
        <motion.div style={{x: w6X, y: w6Y, scale: w6Scale, rotate: w6Rotate, opacity: w6Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">مرحبا</motion.div>
        <motion.div style={{x: w7X, y: w7Y, scale: w7Scale, rotate: w7Rotate, opacity: w7Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">Olá</motion.div>
        <motion.div style={{x: w8X, y: w8Y, scale: w8Scale, rotate: w8Rotate, opacity: w8Opacity, ...wordStyle}} className="text-5xl sm:text-6xl md:text-8xl absolute whitespace-nowrap select-none">你好</motion.div>

        {/* Nexum Voice */}
        <div className="absolute flex flex-col items-center gap-4 md:gap-6 px-4">
          <motion.div
            style={{
              opacity: presentamosOpacity,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 300,
              letterSpacing: '0.15em',
              color: 'rgba(120, 130, 135, 0.5)',
              textTransform: 'uppercase'
            }}
            className="text-sm md:text-sm"
          >
            {lang === 'es' ? 'Presentamos' : 'Introducing'}
          </motion.div>

          <div className="text-5xl sm:text-5xl md:text-6xl" style={{fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, letterSpacing: '-0.08em', position: 'relative'}}>
            <motion.span style={{opacity: n1O, scale: n1S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>N</motion.span>
            <motion.span style={{opacity: n2O, scale: n2S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>e</motion.span>
            <motion.span style={{opacity: n3O, scale: n3S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>x</motion.span>
            <motion.span style={{opacity: n4O, scale: n4S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>u</motion.span>
            <motion.span style={{opacity: n5O, scale: n5S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>m</motion.span>
            <motion.span style={{opacity: n1O, scale: n1S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}> </motion.span>
            <motion.span style={{opacity: n7O, scale: n7S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>V</motion.span>
            <motion.span style={{opacity: n8O, scale: n8S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>o</motion.span>
            <motion.span style={{opacity: n9O, scale: n9S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>i</motion.span>
            <motion.span style={{opacity: n10O, scale: n10S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>c</motion.span>
            <motion.span style={{opacity: n11O, scale: n11S, color: 'rgba(100, 115, 120, 0.95)', display: 'inline-block', position: 'relative', zIndex: 1}}>e</motion.span>

            <motion.div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: glowOpacity,
                background: 'radial-gradient(ellipse, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                filter: 'blur(30px)',
                pointerEvents: 'none',
                zIndex: 0
              }}
              className="w-[250px] h-[100px] md:w-[400px] md:h-[150px]"
            />
          </div>

          <motion.div
            style={{
              opacity: taglineOpacity,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 300,
              letterSpacing: '0.02em',
              color: 'rgba(140, 145, 150, 0.7)',
              textAlign: 'center',
              lineHeight: 1.6
            }}
            className="text-sm md:text-base max-w-[320px] md:max-w-[550px]"
          >
            {lang === 'es' ? 'Agenda citas, atiende llamadas en más de 20 idiomas y recupera clientes inactivos, incluso fuera del horario comercial. Nuestro agente les avisa de cambio de aceite, campañas, e incluso conoce el historial de cada vehículo y sus necesidades particulares.' : 'Schedule appointments, answer calls in over 20 languages and recover inactive clients, even outside business hours. Our agent notifies them about oil changes, campaigns, and even knows each vehicle\'s history and particular needs.'}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== HERO ANIMATION WRAPPER ====================
const HeroAnimation = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile ? null : <HeroAnimationDesktop />;
};

// Mobile: clean security cards. Desktop: full cinematic lock animation.
const SecuritySection = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile ? <EnterpriseSecuritySection /> : <LockAnimationInSection />;
};

// ==================== VIDEO + CHAT DEMO COMPONENT ====================
const VideoChatDemo = ({ lang }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const messagesStartedRef = useRef(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [videoBlurred, setVideoBlurred] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [lastFrameUrl, setLastFrameUrl] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(true);
  
  const messages = [
    { type: 'nexum', text: 'Buenas tardes, BMW Premium Barcelona. ¿En qué puedo ayudarle?' },
    { type: 'user', text: 'Hola, quería pedir cita para la revisión de mi X3' },
    { type: 'nexum', text: 'Por supuesto. Veo que su BMW X3 tiene 47.000 km y la última revisión fue hace 11 meses.' },
    { type: 'nexum', text: '¿Le vendría bien el <strong>jueves 12 a las 9:30</strong>?' },
    { type: 'user', text: 'Sí, perfecto' },
    { type: 'nexum', text: '¿Necesitará vehículo de cortesía?' },
    { type: 'user', text: 'No hace falta, gracias' },
    { type: 'nexum', text: '<span class="text-emerald-600 font-semibold">Perfecto, cita confirmada.</span> Le acabo de enviar la confirmación por WhatsApp. ¿Algo más?' },
    { type: 'user', text: 'Nada más, gracias' },
    { type: 'nexum', text: 'Gracias a usted. ¡Que tenga buen día!' },
  ];
  
  // Intersection Observer - start video; chat starts on video's real onPlay
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
            
            // Start video (robust: retry if not ready yet)
            if (videoRef.current) {
              const v = videoRef.current;
              v.muted = true;
              const p = v.play();
              if (p && p.catch) {
                p.catch(() => {
                  // If not ready yet, play as soon as it can
                  v.addEventListener('canplay', () => v.play().catch(() => {}), { once: true });
                });
              }
            }
          }
        });
      },
      { threshold: 0.3 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [hasStarted]);
  
  // Capture the current video frame to a canvas -> data URL (used to freeze last frame)
  const captureLastFrame = () => {
    try {
      const v = videoRef.current;
      const canvas = canvasRef.current;
      if (!v || !canvas || !v.videoWidth) return;
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      setLastFrameUrl(canvas.toDataURL('image/jpeg', 0.9));
    } catch (err) {
      // Frame capture can fail if the video is tainted by CORS; fallback = pause on last frame
      console.warn('captureLastFrame failed (likely CORS):', err);
    }
  };
  
  // Handle video time update - blur 0.5s before end, capture last frame, then freeze before natural "ended"
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const timeLeft = v.duration - v.currentTime;
    
    if (timeLeft <= 0.5 && !videoBlurred) {
      setVideoBlurred(true);
    }
    
    // Capture the frame just before end and pause to freeze it (avoids browser resetting on 'ended')
    if (timeLeft <= 0.12 && !videoEnded) {
      captureLastFrame();
      setVideoEnded(true);
      try { v.pause(); } catch (err) { console.warn('video pause failed:', err); }
    }
  };

  // Safety net: if video reaches native end, keep it paused and don't reset currentTime.
  const handleVideoEnded = () => {
    if (!videoEnded) {
      captureLastFrame();
      setVideoEnded(true);
    }
    setVideoBlurred(true);
  };
  
  // When video actually starts playing, kick off the chat animation (perfect sync)
  const handleVideoPlay = () => {
    if (messagesStartedRef.current) return;
    messagesStartedRef.current = true;
    setTimeout(() => animateMessages(), 700);
  };
  
  // Animate messages with typing indicator - SLOWER, syncs speaking indicator
  const animateMessages = () => {
    let delay = 0;
    
    messages.forEach((msg, index) => {
      // Show typing indicator before nexum messages
      if (msg.type === 'nexum') {
        setTimeout(() => {
          setIsTyping(true);
          setIsSpeaking(true); // Nexum "speaking" in the video
        }, delay);
        delay += 1100; // Typing duration (slower)
        setTimeout(() => {
          setIsTyping(false);
          setVisibleMessages(prev => prev + 1);
        }, delay);
        delay += 750; // Pause after message (slower)
      } else {
        // User messages appear with slight delay
        setTimeout(() => {
          setIsSpeaking(false); // Client "speaking"
          setVisibleMessages(prev => prev + 1);
        }, delay);
        delay += 1200; // Pause after user message (slower)
      }
    });
    
    // Show footer after all messages
    setTimeout(() => setShowFooter(true), delay + 500);
  };
  
  return (
    <div ref={containerRef} className="relative w-full">
      {/* Main container - side by side layout */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch">
        
        {/* LEFT: Video (vertical) */}
        <div className="relative w-full md:w-[280px] lg:w-[320px] flex-shrink-0">
          <div className="absolute -inset-4 bg-zinc-400/20 rounded-3xl blur-2xl" />
          <div 
            className="relative overflow-hidden rounded-2xl border border-white/40 shadow-2xl bg-zinc-200"
            style={{ 
              aspectRatio: '9/16',
              maxHeight: '520px'
            }}
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              style={{ 
                filter: videoBlurred ? 'blur(6px)' : 'none',
                transition: 'filter 0.8s ease-out',
                opacity: lastFrameUrl ? 0 : 1
              }}
              muted
              playsInline
              preload="auto"
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnded}
              onPlay={handleVideoPlay}
            >
              <source src="https://customer-assets-39nsmqrw.emergentagent.net/job_brand-home-10/artifacts/y1o838ou_Video%201.mp4" type="video/mp4" />
            </video>
            
            {/* Frozen last-frame overlay (guarantees the video never disappears) */}
            {lastFrameUrl && (
              <img
                src={lastFrameUrl}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                  filter: 'blur(6px)',
                  transition: 'opacity 0.4s ease-out'
                }}
              />
            )}
            
            {/* Hidden canvas for frame capture */}
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Live speaking / listening indicator - ties chat state to video */}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
              <motion.span
                className={`w-1.5 h-1.5 rounded-full ${isSpeaking ? 'bg-emerald-400' : 'bg-sky-400'}`}
                animate={{ scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className="text-[10px] font-medium text-white/90 tracking-wide">
                {isSpeaking 
                  ? (lang === 'es' ? 'Nexum habla' : 'Nexum speaking')
                  : (lang === 'es' ? 'Escuchando' : 'Listening')}
              </span>
            </div>
          </div>
        </div>
        
        {/* RIGHT: Chat */}
        <div className="relative flex-1 min-w-0">
          <div className="absolute -inset-4 bg-zinc-400/20 rounded-3xl blur-2xl" />
          
          <div 
            className="relative bg-white/30 backdrop-blur-2xl overflow-hidden rounded-2xl border border-white/40 shadow-2xl h-full"
            style={{ backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/20">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-zinc-800">BMW Premium Barcelona</span>
              </div>
              <span className="text-[10px] text-zinc-500">Nexum AI</span>
            </div>
            
            {/* Chat Content */}
            <div className="p-4 md:p-5 overflow-y-auto" style={{ maxHeight: '420px' }}>
              <div className="space-y-2.5">
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={visibleMessages > idx 
                      ? { opacity: 1, y: 0, scale: 1 } 
                      : { opacity: 0, y: 12, scale: 0.96 }
                    }
                    transition={{ 
                      duration: 0.35, 
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                  >
                    {msg.type === 'nexum' ? (
                      <div className="flex gap-2 items-end">
                        <div className="w-6 h-6 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0">
                          <span className="text-[9px] font-bold text-white">N</span>
                        </div>
                        <div className="bg-zinc-100/90 rounded-2xl rounded-bl-md px-3 py-2 max-w-[85%]">
                          <p
                            className="text-zinc-800 text-[13px] leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(msg.text, {
                                ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'span', 'br'],
                                ALLOWED_ATTR: ['class'],
                              }),
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <div className="bg-zinc-900 rounded-2xl rounded-br-md px-3 py-2 max-w-[85%]">
                          <p className="text-white text-[13px] leading-relaxed">{msg.text}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {/* Typing indicator */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: isTyping ? 1 : 0, y: isTyping ? 0 : 8 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 items-end"
                >
                  <div className="w-6 h-6 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-white">N</span>
                  </div>
                  <div className="bg-zinc-100/90 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.span
                          key={i}
                          className="w-1.5 h-1.5 bg-zinc-400 rounded-full"
                          animate={{ y: [0, -4, 0] }}
                          transition={{ 
                            duration: 0.5, 
                            repeat: Infinity, 
                            delay: i * 0.15,
                            ease: "easeInOut"
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Footer */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: showFooter ? 1 : 0 }}
                transition={{ duration: 0.4 }}
                className="mt-4 pt-3 border-t border-zinc-200/50 flex items-center justify-between text-[11px] text-zinc-400"
              >
                <span>48 segundos</span>
                <span className="flex items-center gap-1 text-emerald-600 font-medium">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Cita agendada
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== VOICE DEMO SECTION ====================
const VoiceDemoSection = () => {
  const { lang } = useLanguage();
  const [activeVoice, setActiveVoice] = useState(null);
  
  const voices = [
    { id: 'es', flag: 'https://flagcdn.com/w80/es.png', name: 'Sara', accent: 'Española' },
    { id: 'en', flag: 'https://flagcdn.com/w80/gb.png', name: 'Emma', accent: 'British' },
    { id: 'fr', flag: 'https://flagcdn.com/w80/fr.png', name: 'Marie', accent: 'Française' },
    { id: 'de', flag: 'https://flagcdn.com/w80/de.png', name: 'Anna', accent: 'Deutsch' },
    { id: 'it', flag: 'https://flagcdn.com/w80/it.png', name: 'Sofia', accent: 'Italiana' },
    { id: 'pt', flag: 'https://flagcdn.com/w80/pt.png', name: 'Ana', accent: 'Portuguesa' },
    { id: 'nl', flag: 'https://flagcdn.com/w80/nl.png', name: 'Lisa', accent: 'Nederlands' },
    { id: 'mx', flag: 'https://flagcdn.com/w80/mx.png', name: 'Valentina', accent: 'Mexicana' },
  ];
  
  // Duplicar para el carrusel infinito
  const allVoices = [...voices, ...voices];
  
  const handlePlay = (id) => {
    if (activeVoice === id) {
      setActiveVoice(null);
    } else {
      setActiveVoice(id);
      setTimeout(() => setActiveVoice(null), 2500);
    }
  };

  const callSteps = [
    { 
      icon: Phone, 
      title: lang === 'es' ? 'Cliente llama' : 'Client calls',
      desc: lang === 'es' ? 'Nexum responde en 1 segundo' : 'Nexum answers in 1 second'
    },
    { 
      icon: MessageCircle, 
      title: lang === 'es' ? 'Entiende el contexto' : 'Understands context',
      desc: lang === 'es' ? 'Historial, vehículo, preferencias' : 'History, vehicle, preferences'
    },
    { 
      icon: CalendarCheck, 
      title: lang === 'es' ? 'Resuelve o agenda' : 'Resolves or books',
      desc: lang === 'es' ? 'Cita confirmada por WhatsApp' : 'Appointment confirmed via WhatsApp'
    },
    { 
      icon: RefreshCw, 
      title: lang === 'es' ? 'Aprende y mejora' : 'Learns and improves',
      desc: lang === 'es' ? 'Cada interacción la hace más lista' : 'Every interaction makes it smarter'
    }
  ];
  
  return (
    <section 
      className="relative py-12 md:py-20 lg:py-28 overflow-visible pb-0 md:!pb-[500vh]" 
      data-testid="voice-demo-section"
      style={{ position: 'relative', zIndex: 10 }}
    >
      {/* Fondo con imagen de nubes */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/o5ndff8m_EA7CADF3-1201-431A-8440-5F5DEAD73B6D%202-2.png)' }}
      />
      {/* Overlay sutil para legibilidad */}
      <div className="absolute inset-0 bg-white/40" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="max-w-6xl mx-auto px-5 md:px-12 lg:px-16 mb-12 md:mb-20">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerFromCenter}
            className="text-center"
          >
            <motion.span variants={blurIn} className="text-[10px] md:text-[11px] font-medium tracking-[0.2em] uppercase text-zinc-600 mb-4 md:mb-6 block">
              {lang === 'es' ? 'Voces IA' : 'AI Voices'}
            </motion.span>
            <motion.h2 variants={scaleFromCenter} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900 mb-3 md:mb-5">
              {lang === 'es' ? 'Voces naturales en +20 idiomas' : 'Natural voices in +20 languages'}
            </motion.h2>
            <motion.p variants={blurIn} className="text-zinc-700 max-w-xl mx-auto text-base md:text-xl leading-relaxed">
              {lang === 'es' 
                ? 'Nexum habla con tus clientes en su idioma, con acentos locales y naturalidad.' 
                : 'Nexum speaks to your customers in their language, with local accents.'}
            </motion.p>
            
            {/* Badge destacado */}
            <motion.div 
              variants={blurIn}
              className="mt-6 md:mt-8 inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full"
            >
              <Phone className="w-4 h-4 text-white" />
              <span className="text-sm text-white font-medium">
                {lang === 'es' ? 'No pierdas más llamadas a tu taller' : "Don't miss any more calls to your shop"}
              </span>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Voice cards - Carrusel lento infinito */}
        <div 
          className="relative overflow-hidden mb-16 md:mb-32 mx-0"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)'
          }}
        >
          <motion.div 
            className="flex gap-2 sm:gap-4"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ 
              x: { 
                duration: 40, 
                repeat: Infinity, 
                ease: 'linear' 
              }
            }}
          >
            {allVoices.map((voice, idx) => (
              <button
                key={`${voice.id}-${idx}`}
                onClick={() => handlePlay(voice.id)}
                className="group relative flex-shrink-0 w-[90px] sm:w-[100px] md:w-[110px] p-3 sm:p-3 md:p-4 transition-all duration-300"
                style={{
                  backgroundColor: activeVoice === voice.id ? 'rgba(24, 24, 27, 0.8)' : 'hsla(0, 0%, 67%, 0.10)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px 0px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Flag circle */}
                <div className="flex justify-center mb-2 sm:mb-2 md:mb-3">
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full overflow-hidden ring-2 transition-all ${
                    activeVoice === voice.id ? 'ring-white' : 'ring-zinc-300 group-hover:ring-zinc-400'
                  }`}>
                    <img 
                      src={voice.flag} 
                      alt={voice.accent}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                {/* Name */}
                <div className="text-center mb-2 sm:mb-2">
                  <p className={`font-medium text-xs sm:text-xs md:text-sm ${activeVoice === voice.id ? 'text-white' : 'text-zinc-900'}`}>
                    {voice.name}
                  </p>
                  <p className={`text-[9px] sm:text-[10px] md:text-[10px] mt-0.5 ${activeVoice === voice.id ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    {voice.accent}
                  </p>
                </div>
                
                {/* Play button / Waveform */}
                <div className="flex items-center justify-center">
                  {activeVoice === voice.id ? (
                    <div className="flex items-center gap-0.5">
                      {[...Array(4)].map((_, i) => (
                        <motion.span 
                          key={i}
                          animate={{ height: [6, 12, 6] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                          className="w-1 bg-white rounded-full"
                          style={{ height: 6 }}
                        />
                      ))}
                    </div>
                  ) : (
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-zinc-400 group-hover:text-zinc-600 transition-colors" fill="currentColor" />
                  )}
                </div>
              </button>
            ))}
            
            {/* More languages card */}
            <div 
              className="flex-shrink-0 w-[80px] sm:w-[100px] md:w-[120px] p-3 sm:p-4 md:p-5 flex flex-col items-center justify-center"
              style={{
                backgroundColor: 'hsla(0, 0%, 67%, 0.10)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px 0px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <span className="text-xl sm:text-2xl md:text-3xl font-grotesk font-bold text-zinc-900 mb-1">+12</span>
              <span className="text-[9px] sm:text-[10px] md:text-xs text-zinc-600">{lang === 'es' ? 'idiomas más' : 'more languages'}</span>
            </div>
            <div 
              className="flex-shrink-0 w-[80px] sm:w-[100px] md:w-[120px] p-3 sm:p-4 md:p-5 flex flex-col items-center justify-center"
              style={{
                backgroundColor: 'hsla(0, 0%, 67%, 0.10)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px 0px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <span className="text-xl sm:text-2xl md:text-3xl font-grotesk font-bold text-zinc-900 mb-1">+12</span>
              <span className="text-[9px] sm:text-[10px] md:text-xs text-zinc-600">{lang === 'es' ? 'idiomas más' : 'more languages'}</span>
            </div>
          </motion.div>
        </div>

        {/* NEW: AI Learning Section */}
        <div className="max-w-6xl mx-auto px-5 md:px-12 lg:px-16">

          {/* Video + Chat Demo - Full width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 md:mb-16"
          >
            <VideoChatDemo lang={lang} />
          </motion.div>
          
          {/* Text below */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center mb-12 md:mb-16"
          >
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-grotesk font-semibold text-zinc-900 mb-2 leading-tight">
              {lang === 'es' 
                ? 'Disponible 24/7.' 
                : 'Available 24/7.'}
            </h3>
            <p className="text-sm text-zinc-500">
              {lang === 'es' 
                ? 'Fines de semana y festivos incluidos.' 
                : 'Weekends and holidays included.'}
            </p>
          </motion.div>

          {/* Mini Call Process - Compact horizontal */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="relative"
          >
            <div className="flex flex-row gap-3 w-full">
              {callSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={`call-step-${idx}`}
                    variants={fadeIn}
                    className="flex-1 relative"
                    style={{
                      backgroundColor: 'hsla(0, 0%, 67%, 0.15)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      padding: '1.25rem 1rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px 0px'
                    }}
                  >
                    {/* Number badge */}
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#000',
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '2px'
                    }}>
                      {idx + 1}
                    </div>
                    
                    {/* Icon */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(255,255,255,0.5)',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 0.75rem',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }}>
                      <Icon style={{ width: '24px', height: '24px', color: '#000' }} strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-grotesk" style={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 600, 
                      color: '#000',
                      margin: 0,
                      lineHeight: 1.3,
                      textAlign: 'center'
                    }}>
                      {step.title}
                    </h3>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
        
        {/* Certificaciones ISO */}
        <div className="flex items-center justify-center gap-8 md:gap-12 mt-16 md:mt-20">
          <div className="flex items-center gap-2 text-zinc-700">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[11px] md:text-xs font-medium tracking-wide">ISO 27001</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-700">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[11px] md:text-xs font-medium tracking-wide">ISO 42001</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-700">
            <Check className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[11px] md:text-xs font-medium tracking-wide">Ready</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== LOCK SCROLL ANIMATION SECTION ====================
const lerp = (a, b, t) => a + (b - a) * t;

// Original Lottie JSON - 3D flip animation
const LOCK_DATA = {"v":"4.8.0","meta":{"g":"LottieFiles AE ","a":"","k":"","d":"","tc":""},"fr":29.9700012207031,"ip":0,"op":80.0000032584668,"w":500,"h":500,"nm":"Lock","ddd":1,"assets":[],"layers":[{"ddd":0,"ind":1,"ty":3,"nm":"Null 1","sr":1,"ks":{"o":{"a":0,"k":0,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":1,"k":[{"i":{"x":0,"y":1},"o":{"x":0.333,"y":0},"t":10,"s":[472,58,0],"to":[-6.667,0,0],"ti":[6.667,0,0]},{"i":{"x":0,"y":0},"o":{"x":0.333,"y":0.333},"t":25,"s":[432,58,0],"to":[0,0,0],"ti":[0,0,0]},{"i":{"x":0.667,"y":1},"o":{"x":1,"y":0},"t":45,"s":[432,58,0],"to":[6.667,0,0],"ti":[-6.667,0,0]},{"t":60.0000024438501,"s":[472,58,0]}],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[263,263,100],"ix":6}},"ao":0,"ip":0,"op":155.000006313279,"st":5.00000020365417,"bm":0},{"ddd":1,"ind":2,"ty":4,"nm":"Top","parent":1,"sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"rx":{"a":0,"k":0,"ix":8},"ry":{"a":1,"k":[{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":5,"s":[0]},{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":25,"s":[-180]},{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":45,"s":[-180]},{"t":70.0000028511585,"s":[0]}],"ix":9},"rz":{"a":0,"k":0,"ix":10},"or":{"a":0,"k":[0,0,0],"ix":7},"p":{"a":0,"k":[-61.625,68.22,0],"ix":2},"a":{"a":0,"k":[23.375,17.625,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[15.68,0],[0.01,0],[0,0],[0,0],[0,0],[0,0],[0,-15.68],[0,0],[-1.04,-1.03],[-1.58,0],[0,3.17],[0,0],[-9.34,-0.01],[0,0],[0,0],[0,0],[-0.01,-9.35],[0,0],[-3.17,0],[-1.04,1.04],[0,1.59],[0,0]],"o":[[0,0],[0,0],[0,0],[0,0],[-0.01,0],[-15.68,0],[0,0],[0,1.59],[1.04,1.04],[3.17,0],[0,0],[0.01,-9.35],[0,0],[0,0],[0,0],[9.34,-0.01],[0,0],[0,3.17],[1.58,0],[1.04,-1.03],[0,0],[0,-15.68]],"v":[[0.72,-31.055],[0.7,-31.055],[0.11,-31.055],[-0.11,-31.055],[-0.7,-31.055],[-0.72,-31.055],[-29.11,-2.665],[-29.11,19.544],[-27.42,23.594],[-23.37,25.274],[-17.63,19.534],[-17.63,-2.665],[-0.69,-19.575],[-0.11,-19.575],[0.11,-19.575],[0.69,-19.575],[17.63,-2.665],[17.63,20.815],[23.37,26.555],[27.42,24.875],[29.11,20.825],[29.11,-2.665]],"c":true},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"fl","c":{"a":0,"k":[0,0,0,1],"ix":4},"o":{"a":0,"k":100,"ix":5},"r":1,"bm":0,"nm":"Fill 1","mn":"ADBE Vector Graphic - Fill","hd":false},{"ty":"tr","p":{"a":0,"k":[0,0],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Group 1","np":2,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false}],"ip":0,"op":155.000006313279,"st":5.00000020365417,"bm":0},{"ddd":0,"ind":3,"ty":4,"nm":"Key-Hole V 2","parent":1,"sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":1,"k":[{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":5,"s":[0]},{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":25,"s":[180]},{"i":{"x":[0.667],"y":[1]},"o":{"x":[0.333],"y":[0]},"t":45,"s":[180]},{"t":65.0000026475043,"s":[0]}],"ix":10},"p":{"a":0,"k":[-84.875,100.648,0],"ix":2},"a":{"a":0,"k":[4.795,7.068,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[2.511,0],[0,-2.51],[0,0],[-2.51,0],[0,2.511],[0,0]],"o":[[-2.51,0],[0,0],[0,2.511],[2.511,0],[0,0],[0,-2.51]],"v":[[0,-6.818],[-4.546,-2.273],[-4.546,2.273],[0,6.818],[4.546,2.273],[4.546,-2.273]],"c":true},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"fl","c":{"a":0,"k":[1,1,1,1],"ix":4},"o":{"a":0,"k":100,"ix":5},"r":1,"bm":0,"nm":"Fill 1","mn":"ADBE Vector Graphic - Fill","hd":false},{"ty":"tr","p":{"a":0,"k":[4.795,7.068],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Group 1","np":2,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false}],"ip":0,"op":155.000006313279,"st":5.00000020365417,"bm":0},{"ddd":0,"ind":4,"ty":4,"nm":"Bottom","parent":1,"sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[-84.875,100.648,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,-8.56],[0,0],[6.87,-1.58],[1.21,0],[0,0],[1.38,0.41],[0,7.05],[0,0],[-7.28,1.27],[-0.92,0],[0,0]],"o":[[0,0],[0,7.35],[-1.13,0.27],[0,0],[-1.51,0],[-6.44,-1.88],[0,0],[0,-7.64],[0.87,-0.16],[0,0],[8.56,0]],"v":[[42,-17],[42,17],[30.01,32.09],[26.5,32.5],[-26.5,32.5],[-30.86,31.87],[-42,17],[-42,-17],[-29.19,-32.26],[-26.5,-32.5],[26.5,-32.5]],"c":true},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"fl","c":{"a":0,"k":[0,0,0,1],"ix":4},"o":{"a":0,"k":100,"ix":5},"r":1,"bm":0,"nm":"Fill 1","mn":"ADBE Vector Graphic - Fill","hd":false},{"ty":"tr","p":{"a":0,"k":[0,0],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Group 1","np":2,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false}],"ip":0,"op":155.000006313279,"st":5.00000020365417,"bm":0}],"markers":[]};

const LockScrollSection = () => {
  const { lang } = useLanguage();
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const animRef = useRef(null);
  const scrollSectionRef = useRef(null);
  const smoothTotal = useRef(0);
  const rafRef = useRef(0);
  const [phase2, setPhase2] = useState(0);
  const [lockProgress, setLockProgress] = useState(0);

  // Initialize Lottie with HTML renderer for 3D support (bundled via npm)
  useEffect(() => {
    if (!containerRef.current) return;
    animRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "html",
      loop: false,
      autoplay: false,
      animationData: LOCK_DATA,
    });
    return () => animRef.current?.destroy();
  }, []);

  // Scroll animation loop
  useEffect(() => {
    const getTarget = () => {
      const section = scrollSectionRef.current;
      if (!section) return 0;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight - window.innerHeight;
      return sectionHeight > 0 ? Math.min(Math.max(-rect.top / sectionHeight, 0), 1) : 0;
    };
    
    const tick = () => {
      smoothTotal.current = lerp(smoothTotal.current, getTarget(), 0.22);
      const total = smoothTotal.current;
      const p1 = Math.min(total / 0.6, 1);
      const p2 = Math.min(Math.max((total - 0.6) / 0.4, 0), 1);
      
      // Lottie animation: frame 25 (open) to frame 5 (closed)
      animRef.current?.goToAndStop(25 - p1 * 20, true);
      
      // Hide first child (static lock) at start, show at end
      if (containerRef.current && containerRef.current.children[0]) {
        containerRef.current.children[0].style.opacity = p1 < 0.9 ? '0' : '1';
      }
      
      setLockProgress(p1);
      
      if (wrapperRef.current) {
        wrapperRef.current.style.transform = `translateY(${(1 - p1) * 120}px)`;
        wrapperRef.current.style.opacity = String(p1 < 0.2 ? (p1 / 0.2) * 0.3 : 1);
      }
      setPhase2(p2);
      rafRef.current = requestAnimationFrame(tick);
    };
    
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Static lock opacity: invisible at start, visible when animation ends
  const staticLockOpacity = lockProgress;

  return (
    <div 
      ref={scrollSectionRef} 
      style={{ height: "400vh", position: "relative", background: "#000" }}
      data-testid="lock-scroll-section"
    >
      <div style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}>
        {/* Background image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/o5ndff8m_EA7CADF3-1201-431A-8440-5F5DEAD73B6D%202-2.png" 
          alt="Sky" 
          loading="lazy"
          decoding="async"
          style={{ 
            position: "absolute", 
            inset: 0, 
            width: "100%", 
            height: "100%", 
            objectFit: "cover", 
            objectPosition: "center", 
            filter: "brightness(0.55) saturate(0.7)"
          }} 
        />
        
        {/* Radial vignette */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)" }} />
        
        {/* Bottom gradient */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "30%", background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.6))" }} />
        
        {/* Content */}
        <div style={{ 
          position: "absolute", 
          inset: 0, 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center" 
        }}>
          {/* Lock Container */}
          <div style={{ position: "relative", width: 160, height: 160, marginBottom: "-0.8rem" }}>
            <div 
              ref={containerRef}
              style={{ 
                width: "100%", 
                height: "100%",
                filter: "invert(1)"
              }}
            />
          </div>
          
          {/* Text Wrapper */}
          <div 
            ref={wrapperRef}
            style={{ 
              position: "relative", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              transform: "translateY(120px)",
              opacity: 0,
              willChange: "transform, opacity"
            }}
          >
            {/* Main Title */}
            <h1 
              className="font-grotesk"
              style={{ 
                color: "#fff", 
                fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)", 
                fontWeight: 500, 
                letterSpacing: "-0.02em", 
                whiteSpace: "nowrap", 
                margin: 0, 
                opacity: Math.max(1 - phase2 * 3, 0)
              }}
            >
              {lang === 'es' ? 'Preparado para los estándares más exigentes' : 'Ready for the most demanding standards'}
            </h1>
            
            {/* Animated Dots */}
            <div style={{ 
              position: "absolute", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              gap: 14 + (1 - phase2) * 46,
              pointerEvents: "none" 
            }}>
              {Array.from({ length: 8 }).map((_, i) => {
                const p = Math.min(Math.max((phase2 - i * 0.1) / 0.15, 0), 1);
                return (
                  <div 
                    key={i} 
                    style={{ 
                      width: 13, 
                      height: 13, 
                      borderRadius: "50%", 
                      background: "#fff", 
                      opacity: p, 
                      transform: `scale(${0.2 + p * 0.8})`
                    }} 
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== HOW IT WORKS SECTION ====================
// ==================== PROCESS SECTION ====================
const ProcessSection = () => {
  const { lang } = useLanguage();
  
  const steps = [
    {
      num: '01',
      title: lang === 'es' ? 'Configuración' : 'Setup',
      desc: lang === 'es' 
        ? 'Conectamos Nexum con tu agenda, WhatsApp y sistemas existentes en menos de 24 horas.'
        : 'We connect Nexum with your calendar, WhatsApp and existing systems in less than 24 hours.'
    },
    {
      num: '02', 
      title: lang === 'es' ? 'Entrenamiento' : 'Training',
      desc: lang === 'es'
        ? 'Personalizamos las respuestas, tono y flujos según tu negocio y marca.'
        : 'We customize responses, tone and flows according to your business and brand.'
    },
    {
      num: '03',
      title: lang === 'es' ? 'Activación' : 'Activation', 
      desc: lang === 'es'
        ? 'Nexum empieza a atender llamadas, agendar citas y hacer seguimiento automáticamente.'
        : 'Nexum starts answering calls, scheduling appointments and following up automatically.'
    }
  ];
  
  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-white" data-testid="process-section">
      <div className="max-w-6xl mx-auto px-5 md:px-12 lg:px-16">
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, amount: 0.3 }} 
          variants={stagger} 
          className="text-center mb-16 md:mb-20"
        >
          <motion.span variants={blurIn} className="text-[10px] md:text-[11px] font-medium tracking-[0.2em] uppercase text-zinc-400 mb-4 md:mb-6 block">
            {lang === 'es' ? 'Proceso' : 'Process'}
          </motion.span>
          <motion.h2 variants={scaleFromCenter} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900">
            {lang === 'es' ? 'Cómo empezamos' : 'How we start'}
          </motion.h2>
        </motion.div>

        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, amount: 0.2 }} 
          variants={staggerFromCenter} 
          className="grid lg:grid-cols-3 gap-5"
        >
          {steps.map((step, idx) => (
            <motion.div 
              key={`process-${idx}`} 
              variants={expandFromCenter}
              whileHover={{ y: -4 }}
              className="bg-zinc-50 border border-zinc-100 p-8 transition-all duration-300"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}
            >
              <span className="text-5xl font-grotesk font-bold text-zinc-200 block mb-5">
                {step.num}
              </span>
              <h3 className="text-lg font-grotesk font-semibold text-zinc-900 mb-2">{step.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== TESTIMONIALS SECTION ====================
const TestimonialsSection = () => {
  const { t } = useLanguage();
  
  const clientPhotos = [
    "https://images.unsplash.com/photo-1631377307479-99d966c84eff?w=200&h=200&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1705645930353-0e335311ef20?w=200&h=200&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1729250829666-2fa142d19e98?w=200&h=200&fit=crop&crop=face"
  ];
  
  return (
    <section className="py-28 md:py-36 bg-[#fafafa]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerFromCenter} className="text-center mb-16">
          <motion.span variants={blurIn} className="text-[11px] font-medium tracking-[0.2em] uppercase text-zinc-400 mb-6 block">{t.testimonials.overline}</motion.span>
          <motion.h2 variants={scaleFromCenter} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900">{t.testimonials.title}</motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={staggerFromCenter} className="grid md:grid-cols-3 gap-5">
          {t.testimonials.items.map((item, idx) => (
            <motion.div 
              key={`testimonial-${idx}`} 
              variants={expandFromCenter}
              whileHover={{ y: -4 }}
              className="bg-white border border-zinc-200 p-8 hover:border-zinc-300 transition-all duration-300"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
            >
              <div className="flex gap-0.5 mb-5">
                {[...Array(item.rating)].map((_, i) => (
                  <Star key={`star-${idx}-${i}`} className="w-4 h-4 fill-zinc-900 text-zinc-900" />
                ))}
              </div>
              <p className="text-zinc-600 leading-relaxed mb-6 text-sm">"{item.text}"</p>
              <div className="pt-5 border-t border-zinc-100 flex items-center gap-3">
                <img 
                  src={clientPhotos[idx]} 
                  alt={item.author}
                  loading="lazy"
                  decoding="async"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-zinc-900 text-sm">{item.author}</p>
                  <p className="text-xs text-zinc-500">{item.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== DASHBOARD SECTION ====================
const DashboardSection = () => {
  const { t, lang } = useLanguage();
  
  return (
    <section className="section-padding bg-light" data-testid="dashboard-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center mb-16">
          <motion.span variants={blurIn} className="overline mb-5 block">{t.dashboard.overline}</motion.span>
          <motion.h2 variants={blurIn} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900 mb-5">{t.dashboard.title}</motion.h2>
          <motion.p variants={blurIn} className="text-lg text-zinc-500 max-w-xl mx-auto">{t.dashboard.subtitle}</motion.p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true, amount: 0.2 }} 
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          {/* Shadow layers behind */}
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-800 transform rotate-1 scale-[1.02] opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-700 transform -rotate-1 scale-[1.01] opacity-15" />
          <div 
            className="absolute inset-0"
            style={{
              boxShadow: '0 50px 100px -20px rgba(0, 0, 0, 0.5), 0 30px 60px -30px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.05)'
            }}
          />
          
          {/* Main Dashboard Container - Sierra Style */}
          <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-8 md:p-10 overflow-hidden min-h-[520px] border border-white/10"
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-zinc-700/20 rounded-full blur-[120px]" />
            
            {/* Floating Cards Grid */}
            <div className="relative z-10 grid md:grid-cols-12 gap-4">
              
              {/* Left Column */}
              <div className="md:col-span-4 space-y-4">
                {/* Stats Card */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white/10 backdrop-blur-md p-5 border border-white/10"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-zinc-400 uppercase tracking-wider">{lang === 'es' ? 'Citas Hoy' : 'Today\'s Appointments'}</span>
                    <span className="text-xs text-emerald-400">+12%</span>
                  </div>
                  <p className="text-4xl font-grotesk font-bold text-white">24</p>
                  <div className="mt-3 h-1.5 bg-zinc-700 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: '75%' }}
                      transition={{ delay: 0.5, duration: 1 }}
                      className="h-full bg-emerald-500"
                    />
                  </div>
                </motion.div>
                
                {/* Notification Card */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-emerald-500/20 backdrop-blur-md p-4 border border-emerald-500/30"
                  style={{ boxShadow: '0 8px 32px rgba(16,185,129,0.2)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-white font-medium">{lang === 'es' ? 'Cita confirmada' : 'Appointment confirmed'}</p>
                      <p className="text-xs text-emerald-300">María G. · 10:30 AM</p>
                    </div>
                  </div>
                </motion.div>
                
                {/* Mini Stats */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid grid-cols-2 gap-3"
                >
                  <div className="bg-white/5 p-4 border border-white/5">
                    <p className="text-2xl font-grotesk font-bold text-white">98%</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{lang === 'es' ? 'Respuesta' : 'Response'}</p>
                  </div>
                  <div className="bg-white/5 p-4 border border-white/5">
                    <p className="text-2xl font-grotesk font-bold text-white">4.9</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">Rating</p>
                  </div>
                </motion.div>
              </div>
              
              {/* Center Column - Chat */}
              <div className="md:col-span-5 space-y-4">
                {/* AI Chat Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white p-5"
                  style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 12px 24px -8px rgba(0,0,0,0.3)' }}
                >
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-100">
                    <div className="w-9 h-9 bg-zinc-900 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">Nexum AI</p>
                      <p className="text-xs text-emerald-500 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                        {lang === 'es' ? 'Activo' : 'Active'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-zinc-100 p-3 max-w-[85%]">
                      <p className="text-sm text-zinc-700">
                        {lang === 'es' 
                          ? '¡Hola! Soy el asistente de Clínica Dental. ¿En qué puedo ayudarte?' 
                          : 'Hi! I\'m the Dental Clinic assistant. How can I help you?'}
                      </p>
                    </div>
                    <div className="bg-zinc-900 p-3 max-w-[85%] ml-auto">
                      <p className="text-sm text-white">
                        {lang === 'es' 
                          ? 'Quiero reservar una limpieza para mañana' 
                          : 'I want to book a cleaning for tomorrow'}
                      </p>
                    </div>
                    <div className="bg-zinc-100 p-3 max-w-[85%]">
                      <p className="text-sm text-zinc-700">
                        {lang === 'es' 
                          ? '¡Perfecto! Tengo disponible a las 10:30 o 16:00. ¿Cuál prefieres?' 
                          : 'Perfect! I have 10:30 or 4:00 PM available. Which do you prefer?'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center gap-2">
                    <div className="flex-1 bg-zinc-100 px-3 py-2">
                      <p className="text-sm text-zinc-400">{lang === 'es' ? 'Escribe un mensaje...' : 'Type a message...'}</p>
                    </div>
                    <button className="w-9 h-9 bg-zinc-900 flex items-center justify-center">
                      <Send className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </motion.div>
              </div>
              
              {/* Right Column */}
              <div className="md:col-span-3 space-y-4">
                {/* Languages Card */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white/10 backdrop-blur-md p-4 border border-white/10"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}
                >
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">{lang === 'es' ? 'Idiomas Activos' : 'Active Languages'}</p>
                  <div className="flex flex-wrap gap-2">
                    {['ES', 'EN', 'FR', 'DE'].map((l) => (
                      <span key={l} className="px-2.5 py-1 bg-white/10 rounded text-xs text-white font-medium">{l}</span>
                    ))}
                  </div>
                </motion.div>
                
                {/* Recent Activity */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white/10 backdrop-blur-md p-4 border border-white/10"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}
                >
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">{lang === 'es' ? 'Actividad' : 'Activity'}</p>
                  <div className="space-y-3">
                    {[
                      { action: lang === 'es' ? 'Cita agendada' : 'Booked', client: 'Carlos R.', time: '2m' },
                      { action: lang === 'es' ? 'Recordatorio' : 'Reminder', client: 'Ana M.', time: '5m' },
                      { action: lang === 'es' ? 'Seguimiento' : 'Follow-up', client: 'John S.', time: '8m' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">{item.action}</p>
                          <p className="text-[10px] text-zinc-500">{item.client}</p>
                        </div>
                        <span className="text-[10px] text-zinc-500">{item.time}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
                
                {/* WhatsApp Badge */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-gradient-to-r from-green-500 to-green-600 p-4"
                  style={{ boxShadow: '0 8px 32px rgba(34,197,94,0.3)' }}
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-sm text-white font-medium">WhatsApp</p>
                      <p className="text-xs text-green-100">{lang === 'es' ? 'Conectado' : 'Connected'}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
              
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== VALUE SECTION ====================
const ValueSection = () => {
  const { t } = useLanguage();
  const icons = [TrendingUp, Zap, Calendar, Users, BarChart3, Shield];
  
  return (
    <section className="py-28 md:py-36 bg-[#fafafa]" data-testid="value-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerFromCenter} className="text-center mb-20">
          <motion.span variants={blurIn} className="text-[11px] font-medium tracking-[0.2em] uppercase text-zinc-400 mb-6 block">{t.value.overline}</motion.span>
          <motion.h2 variants={scaleFromCenter} className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-semibold tracking-tight text-zinc-900 mb-4">
            {t.value.title1}
          </motion.h2>
          <motion.p variants={blurIn} className="text-xl md:text-2xl font-grotesk text-zinc-400">{t.value.title2}</motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={staggerFromCenter} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.value.items.map((item, idx) => {
            const Icon = icons[idx];
            return (
              <motion.div 
                key={`value-${idx}`} 
                variants={expandFromCenter}
                className="bg-white border border-zinc-200 p-8 hover:border-zinc-300 hover:shadow-sm transition-all duration-300"
              >
                <div className="w-11 h-11 bg-zinc-100 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-zinc-600" strokeWidth={1.5} />
                </div>
                <h4 className="text-zinc-900 font-semibold text-lg mb-2">{item.title}</h4>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== VIDEO TEXT MASK (REMOVED - video deleted per user request) ====================

// ==================== SHOWCASE IMAGE SECTION ====================
const ShowcaseImageSection = () => {
  const { lang } = useLanguage();
  
  return (
    <section className="relative w-full bg-black" data-testid="showcase-image-section" data-theme="dark">
      <div className="flex flex-col md:flex-row min-h-[70vh] md:min-h-[80vh]">
        {/* Left - Image */}
        <div className="relative w-full md:w-[60%] h-[40vh] md:h-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_brand-home-10/artifacts/u52omas6_IMG_3320.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {/* Blur overlay hacia la derecha en desktop, abajo en móvil */}
          <div className="hidden md:block absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-black via-black/80 to-transparent" />
          <div className="md:hidden absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/80 to-transparent" />
        </div>
        
        {/* Right - Text */}
        <div className="relative w-full md:w-[40%] flex items-center justify-center md:justify-start px-8 md:px-12 lg:px-16 py-16 md:py-0">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md"
          >
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-grotesk font-medium text-white leading-[1.1] tracking-tight">
              {lang === 'es' 
                ? 'Tu concesionario, siempre disponible.' 
                : 'Your dealership, always available.'}
            </h3>
            <p className="mt-4 md:mt-6 text-sm md:text-base text-zinc-500 leading-relaxed">
              {lang === 'es'
                ? '24 horas. 7 días. Sin llamadas perdidas.'
                : '24 hours. 7 days. No missed calls.'}
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Bottom fade to FAQ */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

// ==================== PREMIUM FAQ SECTION ====================
const PremiumFAQSection = () => {
  const { lang } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: lang === 'es' ? '¿Cuánto tiempo tarda la implementación?' : 'How long does implementation take?',
      a: lang === 'es' 
        ? 'Entre 3-5 días laborables. Incluye integración con tu DMS/agenda, configuración personalizada y formación del equipo.'
        : 'Between 3-5 business days. Includes integration with your DMS/calendar, custom configuration and team training.'
    },
    {
      q: lang === 'es' ? '¿Funciona con mi sistema de gestión actual?' : 'Does it work with my current management system?',
      a: lang === 'es'
        ? 'Sí. Nexum se integra con los principales DMS del sector (SAP, QUITER, etc.), Google Calendar, Outlook y cualquier agenda online.'
        : 'Yes. Nexum integrates with major industry DMS (SAP, QUITER, etc.), Google Calendar, Outlook and any online calendar.'
    },
    {
      q: lang === 'es' ? '¿Y si el cliente quiere hablar con una persona?' : 'What if the client wants to talk to a person?',
      a: lang === 'es'
        ? 'Nexum detecta automáticamente cuándo transferir la llamada a tu equipo, pasando todo el contexto de la conversación.'
        : 'Nexum automatically detects when to transfer the call to your team, passing all conversation context.'
    },
    {
      q: lang === 'es' ? '¿Hay permanencia o compromiso mínimo?' : 'Is there a minimum commitment?',
      a: lang === 'es'
        ? 'No. Sin permanencia. Puedes cancelar mensualmente. Creemos que la mejor forma de retenerte es con resultados.'
        : 'No. No lock-in. You can cancel monthly. We believe the best way to retain you is with results.'
    },
    {
      q: lang === 'es' ? '¿Cómo sabe Nexum cuándo toca mantenimiento?' : 'How does Nexum know when maintenance is due?',
      a: lang === 'es'
        ? 'Configuramos las reglas de mantenimiento de cada marca/modelo. Nexum cruza el kilometraje registrado con estas reglas para anticipar necesidades.'
        : 'We configure maintenance rules for each brand/model. Nexum cross-references registered mileage with these rules to anticipate needs.'
    }
  ];
  
  return (
    <section className="py-16 md:py-24 bg-black" data-testid="faq-section" data-theme="dark">
      <div className="max-w-2xl mx-auto px-5 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-[9px] md:text-[10px] font-medium tracking-[0.3em] uppercase text-zinc-500 mb-4 block">
            FAQ
          </span>
          <h2 className="text-xl md:text-2xl font-grotesk font-semibold tracking-tight text-white">
            {lang === 'es' ? 'Preguntas frecuentes' : 'Frequently asked questions'}
          </h2>
        </motion.div>
        
        {/* FAQ Items */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
          className="space-y-px"
        >
          {faqs.map((faq, idx) => (
            <motion.div
              key={`faq-${idx}`}
              variants={fadeIn}
              className="border-b border-zinc-800"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between py-4 text-left hover:opacity-70 transition-opacity"
                data-testid={`faq-${idx}`}
              >
                <span className="font-medium text-white pr-4 text-xs md:text-sm">{faq.q}</span>
                <ChevronDown className={`w-3 h-3 text-zinc-500 flex-shrink-0 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === idx && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                  className="pb-4"
                >
                  <p className="text-zinc-400 leading-relaxed text-xs md:text-sm">{faq.a}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
        
        {/* Contact */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12 pt-8 border-t border-zinc-800"
        >
          <span className="text-[9px] md:text-[10px] font-medium tracking-[0.3em] uppercase text-zinc-500 mb-3 block">
            {lang === 'es' ? '¿Más preguntas?' : 'More questions?'}
          </span>
          <a 
            href="mailto:hello@nexumbusiness.com" 
            className="text-white text-sm md:text-base font-medium hover:text-zinc-300 transition-colors"
          >
            hello@nexumbusiness.com
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== PREMIUM PRICING SECTION ====================
const PremiumPricingSection = () => {
  const { lang } = useLanguage();
  
  const features = [
    lang === 'es' ? 'Integración completa con tu DMS' : 'Full integration with your DMS',
    lang === 'es' ? 'WhatsApp Business API' : 'WhatsApp Business API',
    lang === 'es' ? 'Voz natural multilingüe' : 'Multilingual natural voice',
    lang === 'es' ? 'Sistema de citas automático' : 'Automatic appointment system',
    lang === 'es' ? 'Alertas de mantenimiento proactivas' : 'Proactive maintenance alerts',
    lang === 'es' ? 'Seguimiento de clientes inactivos' : 'Inactive client follow-up',
    lang === 'es' ? 'Dashboard de analytics' : 'Analytics dashboard',
    lang === 'es' ? 'Soporte prioritario' : 'Priority support'
  ];
  
  return (
    <section id="pricing" className="relative py-24 md:py-36 bg-black overflow-hidden" data-theme="dark" data-testid="pricing-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-black" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[200px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[10px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-zinc-500 mb-6 block">
              {lang === 'es' ? 'Inversión' : 'Investment'}
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-grotesk font-semibold tracking-tight text-white leading-tight mb-6">
              {lang === 'es' ? 'Implementación premium.' : 'Premium implementation.'}
              <br />
              <span className="text-zinc-900">{lang === 'es' ? 'Resultados reales.' : 'Real results.'}</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg">
              {lang === 'es' 
                ? 'Esto no es una suscripción de software. Es el despliegue completo de un empleado digital dedicado a tu negocio.'
                : 'This is not a software subscription. It\'s the complete deployment of a digital employee dedicated to your business.'}
            </p>
            
            {/* Value Prop */}
            <div className="flex items-center gap-3 text-zinc-400">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm">
                {lang === 'es' ? 'Un cliente recuperado paga tu servicio mensual' : 'One recovered client pays for your monthly service'}
              </span>
            </div>
          </motion.div>
          
          {/* Right - Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative bg-zinc-900 border border-zinc-800 p-8 md:p-10">
              {/* Badge */}
              <div className="absolute -top-4 left-8 bg-white text-black px-4 py-1.5 text-xs font-semibold tracking-wide">
                {lang === 'es' ? 'AUTOMOCIÓN' : 'AUTOMOTIVE'}
              </div>
              
              {/* Implementation */}
              <div className="mb-8 pb-8 border-b border-zinc-800">
                <span className="text-[10px] font-medium tracking-[0.2em] uppercase text-zinc-500 block mb-3">
                  {lang === 'es' ? 'Implementación' : 'Implementation'}
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl md:text-6xl font-grotesk font-bold text-white">€2.650</span>
                  <span className="text-zinc-900">{lang === 'es' ? 'único' : 'one-time'}</span>
                </div>
                <p className="text-sm text-zinc-500 mt-2">
                  {lang === 'es' ? 'Setup, configuración y formación' : 'Setup, configuration and training'}
                </p>
              </div>
              
              {/* Monthly */}
              <div className="mb-8">
                <span className="text-[10px] font-medium tracking-[0.2em] uppercase text-zinc-500 block mb-3">
                  {lang === 'es' ? 'Servicio mensual' : 'Monthly service'}
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl md:text-6xl font-grotesk font-bold text-white">€169</span>
                  <span className="text-zinc-900">/mes</span>
                </div>
                <p className="text-sm text-zinc-500 mt-2">
                  {lang === 'es' ? 'Gestión y optimización continua' : 'Ongoing management and optimization'}
                </p>
              </div>
              
              {/* Features */}
              <ul className="space-y-3 mb-8">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm text-zinc-400">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" strokeWidth={2} />
                    {feature}
                  </li>
                ))}
              </ul>
              
              {/* CTA */}
              <a 
                href={CALENDLY_URL} onClick={openCalendly} 
                className="group flex items-center justify-center gap-3 w-full bg-white text-black hover:bg-zinc-100 transition-all py-4 font-semibold"
                data-testid="pricing-cta"
              >
                {lang === 'es' ? 'Solicitar demo' : 'Request demo'}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== PREMIUM FINAL CTA SECTION ====================
const PremiumFinalCTASection = () => {
  const { lang } = useLanguage();
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: ''
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const T = {
    es: {
      title: 'Solicita una demo',
      subtitle: 'Solo trabajamos con un número limitado de clientes.',
      fields: { name: 'Nombre completo', company: 'Empresa', email: 'Email corporativo', phone: 'Teléfono' },
      submit: 'Solicitar',
      submitting: 'Enviando…',
      done: '✓ Solicitud enviada',
      thanks: '¡Gracias! Te contactaremos en menos de 24h.',
      required: 'Nombre y email son obligatorios.',
      invalidEmail: 'Introduce un email válido.',
      needPrivacy: 'Debes aceptar la Política de Privacidad.',
      error: 'No se ha podido enviar. Inténtalo de nuevo en unos segundos.',
      success: '¡Solicitud recibida!',
      successDesc: 'Te contactaremos por email en menos de 24h. Gracias por confiar en Nexum.',
    },
    en: {
      title: 'Request a demo',
      subtitle: 'We only work with a limited number of clients.',
      fields: { name: 'Full name', company: 'Company', email: 'Corporate email', phone: 'Phone' },
      submit: 'Submit',
      submitting: 'Sending…',
      done: '✓ Request sent',
      thanks: "Thank you! We'll contact you within 24h.",
      required: 'Name and email are required.',
      invalidEmail: 'Please enter a valid email.',
      needPrivacy: 'You must accept the Privacy Policy.',
      error: 'Could not send. Please try again in a few seconds.',
      success: 'Request received!',
      successDesc: "We'll contact you by email within 24h. Thanks for trusting Nexum.",
    },
  }[lang];
  
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || submitted) return;
    
    if (!formData.nombre.trim() || !formData.email.trim()) {
      toast.error(T.required);
      return;
    }
    if (!isValidEmail(formData.email.trim())) {
      toast.error(T.invalidEmail);
      return;
    }
    if (!acceptPrivacy) {
      toast.error(T.needPrivacy);
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: formData.nombre.trim(),
        email: formData.email.trim(),
        phone: formData.telefono.trim() || null,
        company: formData.empresa.trim() || null,
        source: 'final_cta_form',
        lang,
      };
      const res = await fetch('/api/leads/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      setSubmitted(true);
      toast.success(T.success, { description: T.successDesc, duration: 8000 });
      setFormData({ nombre: '', empresa: '', email: '', telefono: '' });
      setAcceptPrivacy(false);
    } catch (err) {
      toast.error(T.error);
      console.error('Lead submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <section id="demo" className="py-16 md:py-24 bg-black" data-testid="final-cta-section" data-theme="dark">
      <div className="max-w-sm mx-auto px-5">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-6">
            <h2 className="text-xl md:text-2xl font-grotesk font-semibold tracking-tight text-white mb-2">
              {T.title}
            </h2>
            <p className="text-zinc-500 text-[11px]">{T.subtitle}</p>
          </div>
          
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="border border-emerald-500/30 bg-emerald-500/5 p-6 text-center"
              data-testid="lead-success"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" strokeWidth={2.5} />
              </div>
              <p className="text-white text-sm font-medium mb-1">{T.done}</p>
              <p className="text-zinc-400 text-[12px] leading-relaxed">{T.thanks}</p>
            </motion.div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="lead-form">
            <input 
              type="text"
              placeholder={T.fields.name}
              value={formData.nombre}
              required
              disabled={submitting}
              onChange={(e) => setFormData({...formData, nombre: e.target.value})}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 disabled:opacity-60"
              data-testid="lead-input-name"
            />
            <input 
              type="text"
              placeholder={T.fields.company}
              value={formData.empresa}
              disabled={submitting}
              onChange={(e) => setFormData({...formData, empresa: e.target.value})}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 disabled:opacity-60"
              data-testid="lead-input-company"
            />
            <input 
              type="email"
              placeholder={T.fields.email}
              value={formData.email}
              required
              disabled={submitting}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 disabled:opacity-60"
              data-testid="lead-input-email"
            />
            <input 
              type="tel"
              placeholder={T.fields.phone}
              value={formData.telefono}
              disabled={submitting}
              onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 disabled:opacity-60"
              data-testid="lead-input-phone"
            />
            
            <button 
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 mt-1 bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="lead-submit"
            >
              {submitting && (
                <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              )}
              {submitting ? T.submitting : T.submit}
            </button>
            
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input 
                type="checkbox" 
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                className="w-3 h-3 bg-zinc-900 border-zinc-700 accent-white"
                data-testid="lead-privacy"
              />
              <span className="text-[10px] text-zinc-600">
                {lang === 'es' ? 'Acepto la ' : 'I accept the '}
                <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white underline underline-offset-2">
                  {lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
                </a>
              </span>
            </label>
          </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  const { lang, t } = useLanguage();
  
  return (
    <footer className="py-16 md:py-20 bg-black border-t border-white/5" data-testid="footer" data-theme="dark">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <span className="text-xl font-grotesk font-semibold text-white tracking-tight">Nexum</span>
            <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
              {lang === 'es' 
                ? 'El empleado digital para tu negocio de automoción.' 
                : 'The digital employee for your automotive business.'}
            </p>
          </div>
          
          {/* Producto */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {lang === 'es' ? 'Producto' : 'Product'}
            </h4>
            <ul className="space-y-2.5">
              <li><a href={CALENDLY_URL} onClick={openCalendly} className="text-sm text-zinc-500 hover:text-white transition-colors">{lang === 'es' ? 'Solicitar demo' : 'Request demo'}</a></li>
              <li><a href="#features" className="text-sm text-zinc-500 hover:text-white transition-colors">{lang === 'es' ? 'Funcionalidades' : 'Features'}</a></li>
              <li><a href="#integrations" className="text-sm text-zinc-500 hover:text-white transition-colors">{lang === 'es' ? 'Integraciones' : 'Integrations'}</a></li>
              <li><a href="#faq" className="text-sm text-zinc-500 hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>
          
          {/* Sectores */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {lang === 'es' ? 'Sectores' : 'Industries'}
            </h4>
            <ul className="space-y-2.5">
              <li><span className="text-sm text-zinc-500">{lang === 'es' ? 'Concesionarios' : 'Dealerships'}</span></li>
              <li><span className="text-sm text-zinc-500">{lang === 'es' ? 'Talleres' : 'Workshops'}</span></li>
              <li><span className="text-sm text-zinc-500">{lang === 'es' ? 'Flotas' : 'Fleets'}</span></li>
              <li><span className="text-sm text-zinc-500">{lang === 'es' ? 'Náutica' : 'Marine'}</span></li>
            </ul>
          </div>
          
          {/* Contacto */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {lang === 'es' ? 'Contacto' : 'Contact'}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="mailto:hello@nexumbusiness.com" className="text-sm text-zinc-500 hover:text-white transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  hello@nexumbusiness.com
                </a>
              </li>
              <li className="text-sm text-zinc-500">Barcelona, España</li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-zinc-600">© {new Date().getFullYear()} Nexum Business S.L. {t.footer.copyright}</p>
            
            {/* Legal Links */}
            <div className="flex items-center gap-6">
              <a href="/privacidad" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                {lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              </a>
              <a href="/terminos" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                {lang === 'es' ? 'Términos de Uso' : 'Terms of Use'}
              </a>
              <a href="/cookies" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                {lang === 'es' ? 'Política de Cookies' : 'Cookie Policy'}
              </a>
              <a href="/legal" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                {lang === 'es' ? 'Aviso Legal' : 'Legal Notice'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== MAIN LANDING PAGE ====================
export default function NexumLanding() {
  const [lang, setLang] = useState(() => localStorage.getItem("nexum-lang") || 'es');
  const t = translations[lang];
  
  // Persist language across pages
  useEffect(() => {
    localStorage.setItem("nexum-lang", lang);
  }, [lang]);
  
  // Calendly postMessage listener - shows a real confirmation toast on booking events
  useEffect(() => {
    const isCalendlyEvent = (e) => e && e.data && typeof e.data === 'object' && e.data.event && e.data.event.indexOf('calendly.') === 0;
    const handler = (e) => {
      if (!isCalendlyEvent(e)) return;
      const ev = e.data.event;
      if (ev === 'calendly.event_scheduled') {
        toast.success(
          lang === 'es' ? '¡Demo reservada!' : 'Demo booked!',
          {
            description: lang === 'es'
              ? 'Te acabamos de enviar la confirmación por email. Nos vemos pronto.'
              : "We just sent you a confirmation email. See you soon.",
            duration: 8000,
          }
        );
      } else if (ev === 'calendly.date_and_time_selected') {
        toast(
          lang === 'es' ? 'Fecha seleccionada' : 'Date selected',
          {
            description: lang === 'es'
              ? 'Rellena tus datos para confirmar la reserva.'
              : 'Fill in your details to confirm the booking.',
            duration: 4000,
          }
        );
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [lang]);
  
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="min-h-screen" style={{ background: '#1a1a1a' }}>
        <Header />
        <main>
          <HeroSection />
          <StatsSection />
          <PilotBanner />
          <PositioningSection />
          <HeroAnimation />
          <VoiceDemoSection />
          <SecuritySection />
          
          {/* Platform Diagram - Tabs with flowcharts */}
          <NexumPlatformDiagram />
          
          {/* Integration Section */}
          <IntegrationSection />
          
          {/* Showcase Image */}
          <ShowcaseImageSection />
          
          <PremiumFAQSection />
          <PremiumFinalCTASection />
        </main>
        <Footer />
        <CalendlyModal />
      </div>
    </LanguageContext.Provider>
  );
}
